import type { getDemoLiveModel } from '../../../methods/src/getDemoLiveModel';
import type { getDemoModelWorkbook } from '../../../methods/src/getDemoModelWorkbook';
import type { getBusinessModel } from '../../../methods/src/getBusinessModel';
import type { requestScenarioReview } from '../../../methods/src/requestScenarioReview';
import type { getMarketModel } from '../../../methods/src/getMarketModel';
import type { saveModelScenario } from '../../../methods/src/saveModelScenario';
import type { deleteModelScenario } from '../../../methods/src/deleteModelScenario';
import type { setModelScenarioShared } from '../../../methods/src/setModelScenarioShared';
import type { reapplyModelScenario } from '../../../methods/src/reapplyModelScenario';
import type { getWorkbookQuote } from '../../../methods/src/getWorkbookQuote';
import type { downloadMarketWorkbook } from '../../../methods/src/downloadMarketWorkbook';
import type { createExcelPackCheckoutSession } from '../../../methods/src/createExcelPackCheckoutSession';
import type { getDrafting } from '../../../methods/src/getDrafting';
import type { exportDraftingDocument } from '../../../methods/src/exportDraftingDocument';
import type { listDrafting } from '../../../methods/src/listDrafting';
import type { createDraftingCheckoutSession } from '../../../methods/src/createDraftingCheckoutSession';
import type { getPrepPlan } from '../../../methods/src/getPrepPlan';
import type { createPrepPlanCheckoutSession } from '../../../methods/src/createPrepPlanCheckoutSession';
import type { setDraftingOffice } from '../../../methods/src/setDraftingOffice';
import type { draftDraftingSection } from '../../../methods/src/draftDraftingSection';
import type { refineDraftingText } from '../../../methods/src/refineDraftingText';
import type { saveDraftingVersion } from '../../../methods/src/saveDraftingVersion';
import type { updateDraftingVersion } from '../../../methods/src/updateDraftingVersion';
import type { pickDraftingVersion } from '../../../methods/src/pickDraftingVersion';
import type { acknowledgeDraftingResponsibility } from '../../../methods/src/acknowledgeDraftingResponsibility';
import type { getDraftingFigureUploadSlot } from '../../../methods/src/getDraftingFigureUploadSlot';
import type { addDraftingFigure } from '../../../methods/src/addDraftingFigure';
import type { importDraftingFigures } from '../../../methods/src/importDraftingFigures';
import type { saveDraftingFigures } from '../../../methods/src/saveDraftingFigures';
import type { saveDraftingFigureVersion } from '../../../methods/src/saveDraftingFigureVersion';
import type { reviewDraftingFigure } from '../../../methods/src/reviewDraftingFigure';
import type { redrawDraftingFigure } from '../../../methods/src/redrawDraftingFigure';
import type { saveDraftingWorking } from '../../../methods/src/saveDraftingWorking';
import type { seedDraftingClaims } from '../../../methods/src/seedDraftingClaims';
import type { saveDraftingClaims } from '../../../methods/src/saveDraftingClaims';
import type { addDraftingClaim } from '../../../methods/src/addDraftingClaim';
import type { checkDraftingClaim } from '../../../methods/src/checkDraftingClaim';
import type { runDraftingCheck } from '../../../methods/src/runDraftingCheck';
import type { saveDraftingDetails } from '../../../methods/src/saveDraftingDetails';
import type { getPathway } from '../../../methods/src/getPathway';
import type { savePathwayAnswers } from '../../../methods/src/savePathwayAnswers';
import type { startPathwayStrategy } from '../../../methods/src/startPathwayStrategy';
import type { askPathwayStrategist } from '../../../methods/src/askPathwayStrategist';
import type { createPathwayCheckoutSession } from '../../../methods/src/createPathwayCheckoutSession';
import type { exportPathwayPdf } from '../../../methods/src/exportPathwayPdf';
import type { listPathway } from '../../../methods/src/listPathway';
import type { adminGetPathwayFacts } from '../../../methods/src/adminGetPathwayFacts';
import type { adminReviewPathwayChange } from '../../../methods/src/adminReviewPathwayChange';
import type { adminUpdatePathwayFact } from '../../../methods/src/adminUpdatePathwayFact';
import type { adminRunPathwayWatch } from '../../../methods/src/adminRunPathwayWatch';
import type { adminGetHardwareRules } from '../../../methods/src/adminGetHardwareRules';
import type { adminReviewHardwareChange } from '../../../methods/src/adminReviewHardwareChange';
import type { adminReviewHardwareTerm } from '../../../methods/src/adminReviewHardwareTerm';
import type { adminApproveVerifiedHardware } from '../../../methods/src/adminApproveVerifiedHardware';
import type { adminRunHardwareRules } from '../../../methods/src/adminRunHardwareRules';
import type { updateHardwareProfile } from '../../../methods/src/updateHardwareProfile';
import type { refreshHardwareChecklist } from '../../../methods/src/refreshHardwareChecklist';
import type { createWitnessRequest } from '../../../methods/src/createWitnessRequest';
import type { revokeWitnessRequest } from '../../../methods/src/revokeWitnessRequest';
import type { getWitnessRequest } from '../../../methods/src/getWitnessRequest';
import type { signWitnessRequest } from '../../../methods/src/signWitnessRequest';
import type { confirmWitnessSignature } from '../../../methods/src/confirmWitnessSignature';
import type { verifyInventionLog } from '../../../methods/src/verifyInventionLog';
import type { checkLogRecord } from '../../../methods/src/checkLogRecord';
import type { issueLogPrint } from '../../../methods/src/issueLogPrint';
import type { startConceptionNarrative } from '../../../methods/src/startConceptionNarrative';
import type { saveDesignVersion } from '../../../methods/src/saveDesignVersion';
import type { listDesignVersions } from '../../../methods/src/listDesignVersions';
import type { restoreDesignVersion } from '../../../methods/src/restoreDesignVersion';
import type { getInventionLog } from '../../../methods/src/getInventionLog';
import type { addLogEntry, LogEntryInput } from '../../../methods/src/addLogEntry';
import type { updateLogEntry } from '../../../methods/src/updateLogEntry';
import type { removeLogEntry } from '../../../methods/src/removeLogEntry';
import type { getLogUploadSlot } from '../../../methods/src/getLogUploadSlot';
import type { exportInventionLogPdf } from '../../../methods/src/exportInventionLogPdf';
import { createClient } from '@mindstudio-ai/interface';
import type { CompleteProfileInput } from '../../../methods/src/completeProfile';
import type { UpdateProfileInput } from '../../../methods/src/updateProfile';
import type { CreateIdeaInput } from '../../../methods/src/createIdea';
import type { GetIdeaInput } from '../../../methods/src/getIdea';
import type { UpdateIdeaInput } from '../../../methods/src/updateIdea';
import type { DeleteIdeaInput } from '../../../methods/src/deleteIdea';
import type { ListIdeaVersionsInput } from '../../../methods/src/listIdeaVersions';
import type { ChatTurnInput } from '../../../methods/src/chatTurn';
import type { NeedsDiscoveryChatTurnInput } from '../../../methods/src/needsDiscoveryChatTurn';
import type { ClaimAnonymousSessionInput } from '../../../methods/src/claimAnonymousSession';
import type { ClaimNeedsDiscoveryAnonymousSessionInput } from '../../../methods/src/claimNeedsDiscoveryAnonymousSession';
import type { StartSessionInput } from '../../../methods/src/startSession';
import type { GetSessionInput } from '../../../methods/src/getSession';
import type { SendSessionMessageInput } from '../../../methods/src/sendSessionMessage';
import type { FinalizeSessionInput } from '../../../methods/src/finalizeSession';
import type { GetMemoInput } from '../../../methods/src/getMemo';
import type { ListMemosInput } from '../../../methods/src/listMemos';
import type { GetMemoPdfInput } from '../../../methods/src/getMemoPdf';
import type { SubmitMemoRatingInput } from '../../../methods/src/submitMemoRating';
import type { GetMyMemoRatingInput } from '../../../methods/src/getMyMemoRating';
import type { CompleteGoogleConnectInput } from '../../../methods/src/completeGoogleConnect';
import type { RevokeConnectedAccountInput } from '../../../methods/src/revokeConnectedAccount';
import type { ExportMemoToGoogleDocsInput } from '../../../methods/src/exportMemoToGoogleDocs';
import type { ExportMemoToOneDriveInput } from '../../../methods/src/exportMemoToOneDrive';
import type { StartMicrosoftConnectInput } from '../../../methods/src/startMicrosoftConnect';
import type { CompleteMicrosoftConnectInput } from '../../../methods/src/completeMicrosoftConnect';
import type { GetPlanningBoardInput } from '../../../methods/src/getPlanningBoard';
import type { RefreshTasksFromMemosInput } from '../../../methods/src/refreshTasksFromMemos';
import type { ToggleTaskCompleteInput } from '../../../methods/src/toggleTaskComplete';
import type { UpdateTaskDeadlineInput } from '../../../methods/src/updateTaskDeadline';
import type { UpdateTaskNotesInput } from '../../../methods/src/updateTaskNotes';
import type { GetLearningPathInput } from '../../../methods/src/getLearningPath';
import type { GetLessonInput } from '../../../methods/src/getLesson';
import type { CompleteLessonInput } from '../../../methods/src/completeLesson';
import type { SubmitQuizAttemptInput } from '../../../methods/src/submitQuizAttempt';
import type { JoinWaitlistInput } from '../../../methods/src/joinWaitlist';
import type { SubmitFeedbackInput } from '../../../methods/src/submitFeedback';
import type { ListFeatureRequestsInput } from '../../../methods/src/listFeatureRequests';
import type { GetFeatureRequestInput } from '../../../methods/src/getFeatureRequest';
import type { SubmitFeatureRequestInput } from '../../../methods/src/submitFeatureRequest';
import type { VoteFeatureRequestInput } from '../../../methods/src/voteFeatureRequest';
import type { CommentFeatureRequestInput } from '../../../methods/src/commentFeatureRequest';

import type { getMe } from '../../../methods/src/getMe';
import type { getTimesheetContext } from '../../../methods/src/getTimesheetContext';
import type { getMyTimesheetMonth } from '../../../methods/src/getMyTimesheetMonth';
import type { consultantSaveTimeEntry } from '../../../methods/src/consultantSaveTimeEntry';
import type { consultantDeleteTimeEntry } from '../../../methods/src/consultantDeleteTimeEntry';
import type { consultantSaveMyDetails } from '../../../methods/src/consultantSaveMyDetails';
import type { adminAddConsultant } from '../../../methods/src/adminAddConsultant';
import type { adminResendConsultantInvite } from '../../../methods/src/adminResendConsultantInvite';
import type { adminListConsultants } from '../../../methods/src/adminListConsultants';
import type { adminGetConsultant } from '../../../methods/src/adminGetConsultant';
import type { adminUpdateConsultant } from '../../../methods/src/adminUpdateConsultant';
import type { adminDeleteConsultant } from '../../../methods/src/adminDeleteConsultant';
import type { adminSaveTimeEntry } from '../../../methods/src/adminSaveTimeEntry';
import type { adminDeleteTimeEntry } from '../../../methods/src/adminDeleteTimeEntry';
import type { adminSaveWorkCategory } from '../../../methods/src/adminSaveWorkCategory';
import type { adminSaveEvaluation } from '../../../methods/src/adminSaveEvaluation';
import type { adminDeleteEvaluation } from '../../../methods/src/adminDeleteEvaluation';
import type { completeProfile } from '../../../methods/src/completeProfile';
import type { getReferralInfo } from '../../../methods/src/getReferralInfo';
import type { getActiveAnnouncement, dismissAnnouncement, pingAnnouncementClick } from '../../../methods/src/announcements';
import type {
  adminListAnnouncements,
  adminSaveAnnouncement,
  adminSetAnnouncementStatus,
  adminDeleteAnnouncement,
  adminPreviewAnnouncementAudience,
} from '../../../methods/src/adminAnnouncements';
import type { adminDraftAnnouncement } from '../../../methods/src/adminDraftAnnouncement';
import type { adminGetAnnouncementEngagement } from '../../../methods/src/adminGetAnnouncementEngagement';
import type { updateProfile } from '../../../methods/src/updateProfile';
import type { exportUserData } from '../../../methods/src/exportUserData';
import type { deleteAccount } from '../../../methods/src/deleteAccount';
import type { createIdea } from '../../../methods/src/createIdea';
import type { getIdeaTags } from '../../../methods/src/getIdeaTags';
import type { setIdeaTags, SetIdeaTagsInput } from '../../../methods/src/setIdeaTags';
import type { renameIdeaTag, RenameIdeaTagInput } from '../../../methods/src/renameIdeaTag';
import type { deleteIdeaTag, DeleteIdeaTagInput } from '../../../methods/src/deleteIdeaTag';
import type { pinCollection, PinCollectionInput } from '../../../methods/src/pinCollection';
import type { unpinCollection, UnpinCollectionInput } from '../../../methods/src/unpinCollection';
import type { reorderCollections, ReorderCollectionsInput } from '../../../methods/src/reorderCollections';
import type {
  structureIdeaDraft,
  StructureIdeaDraftInput,
} from '../../../methods/src/structureIdeaDraft';
import type { listIdeas } from '../../../methods/src/listIdeas';
import type { getIdea } from '../../../methods/src/getIdea';
import type { getIdeaCommandCenter } from '../../../methods/src/getIdeaCommandCenter';
import type { getIdeaWorkspace } from '../../../methods/src/getIdeaWorkspace';
import type { getIntelligenceCenter } from '../../../methods/src/getIntelligenceCenter';
import type { getIdeaSignals } from '../../../methods/src/getIdeaSignals';
import type { getIdeaIncubatorMatches } from '../../../methods/src/getIdeaIncubatorMatches';
import type { getSignalsPortfolio } from '../../../methods/src/getSignalsPortfolio';
import type { getRdLedger } from '../../../methods/src/getRdLedger';
import type { rdClockIn } from '../../../methods/src/rdClockIn';
import type { rdClockOut } from '../../../methods/src/rdClockOut';
import type { rdSaveTimeEntry } from '../../../methods/src/rdSaveTimeEntry';
import type { rdDeleteTimeEntry } from '../../../methods/src/rdDeleteTimeEntry';
import type { rdSetHourlyRate } from '../../../methods/src/rdSetHourlyRate';
import type { rdSaveExpense } from '../../../methods/src/rdSaveExpense';
import type { rdDeleteExpense } from '../../../methods/src/rdDeleteExpense';
import type { rdSavePlatformCost } from '../../../methods/src/rdSavePlatformCost';
import type { rdDeletePlatformCost } from '../../../methods/src/rdDeletePlatformCost';
import type { proposeIdeaWatchlist } from '../../../methods/src/proposeIdeaWatchlist';
import type { approveIdeaWatchlist, ApproveIdeaWatchlistInput } from '../../../methods/src/approveIdeaWatchlist';
import type { updateSignalCadence } from '../../../methods/src/updateSignalCadence';
import type { refreshIdeaSignal } from '../../../methods/src/refreshIdeaSignal';
import type { startCategorySignal } from '../../../methods/src/startCategorySignal';
import type { startEmergingSignal } from '../../../methods/src/startEmergingSignal';
import type { promoteEmergingCompetitor } from '../../../methods/src/promoteEmergingCompetitor';
import type { dismissEmergingCompetitor } from '../../../methods/src/dismissEmergingCompetitor';
import type { updateSignalWatch, UpdateSignalWatchInput } from '../../../methods/src/updateSignalWatch';
import type { markSignalSeen } from '../../../methods/src/markSignalSeen';
import type { recordNewsStoryOpen } from '../../../methods/src/recordNewsStoryOpen';
import type { updateIdea } from '../../../methods/src/updateIdea';
import type { deleteIdea } from '../../../methods/src/deleteIdea';
import type { setIdeaFocus, SetIdeaFocusInput } from '../../../methods/src/setIdeaFocus';
import type { setIdeaMarketLens, SetIdeaMarketLensInput } from '../../../methods/src/setIdeaMarketLens';
import type { listIdeaVersions } from '../../../methods/src/listIdeaVersions';
import type { chatTurn } from '../../../methods/src/chatTurn';
import type { needsDiscoveryChatTurn } from '../../../methods/src/needsDiscoveryChatTurn';
import type { claimAnonymousSession } from '../../../methods/src/claimAnonymousSession';
import type { claimNeedsDiscoveryAnonymousSession } from '../../../methods/src/claimNeedsDiscoveryAnonymousSession';
import type { startSession } from '../../../methods/src/startSession';
import type { getSession } from '../../../methods/src/getSession';
import type { sendSessionMessage } from '../../../methods/src/sendSessionMessage';
import type { finalizeSession } from '../../../methods/src/finalizeSession';
import type {
  setSessionTone,
  SetSessionToneInput,
} from '../../../methods/src/setSessionTone';
import type {
  setSessionRegister,
  SetSessionRegisterInput,
} from '../../../methods/src/setSessionRegister';
import type { refreshSessionRegister } from '../../../methods/src/refreshSessionRegister';
import type { getMemo } from '../../../methods/src/getMemo';
import type { listMemos } from '../../../methods/src/listMemos';
import type { getMemoPdf } from '../../../methods/src/getMemoPdf';
import type { submitMemoRating } from '../../../methods/src/submitMemoRating';
import type { getMyMemoRating } from '../../../methods/src/getMyMemoRating';
import type { getPlanningBoard } from '../../../methods/src/getPlanningBoard';
import type { listPlanningTasks } from '../../../methods/src/listPlanningTasks';
import type { updateTaskPriority } from '../../../methods/src/updateTaskPriority';
import type { canvasAssist } from '../../../methods/src/canvasAssist';
import type { setCanvasObjective } from '../../../methods/src/setCanvasObjective';
import type { refreshTasksFromMemos } from '../../../methods/src/refreshTasksFromMemos';
import type { toggleTaskComplete } from '../../../methods/src/toggleTaskComplete';
import type { updateTaskDeadline } from '../../../methods/src/updateTaskDeadline';
import type { updateTaskNotes } from '../../../methods/src/updateTaskNotes';
import type { getProductRating } from '../../../methods/src/getProductRating';
import type { startGoogleConnect } from '../../../methods/src/startGoogleConnect';
import type { completeGoogleConnect } from '../../../methods/src/completeGoogleConnect';
import type { getConnectedAccounts } from '../../../methods/src/getConnectedAccounts';
import type { getCalendarFeed } from '../../../methods/src/getCalendarFeed';
import type { revokeConnectedAccount } from '../../../methods/src/revokeConnectedAccount';
import type { exportMemoToGoogleDocs } from '../../../methods/src/exportMemoToGoogleDocs';
import type { exportMemoToOneDrive } from '../../../methods/src/exportMemoToOneDrive';
import type { detectMailProvider, DetectMailProviderInput } from '../../../methods/src/detectMailProvider';
import type { saveDashboardPrefs, SaveDashboardPrefsInput } from '../../../methods/src/saveDashboardPrefs';
import type { startMicrosoftConnect } from '../../../methods/src/startMicrosoftConnect';
import type { completeMicrosoftConnect } from '../../../methods/src/completeMicrosoftConnect';
import type { startNotionConnect } from '../../../methods/src/startNotionConnect';
import type { completeNotionConnect, CompleteNotionConnectInput } from '../../../methods/src/completeNotionConnect';
import type { listNotionDestinations, ListNotionDestinationsInput } from '../../../methods/src/listNotionDestinations';
import type { sendMemoToNotion, SendMemoToNotionInput } from '../../../methods/src/sendMemoToNotion';
import type { getNotionExport } from '../../../methods/src/getNotionExport';
import type {
  startNeedsDiscoverySession,
  StartNeedsDiscoverySessionInput,
} from '../../../methods/src/startNeedsDiscoverySession';
import type { captureDifferentIdea, CaptureDifferentIdeaInput } from '../../../methods/src/captureDifferentIdea';
import type { moveExampleConversation, MoveExampleConversationInput } from '../../../methods/src/moveExampleConversation';
import type {
  getNeedsDiscoverySession,
  GetNeedsDiscoverySessionInput,
} from '../../../methods/src/getNeedsDiscoverySession';
import type {
  sendNeedsDiscoveryMessage,
  SendNeedsDiscoveryMessageInput,
} from '../../../methods/src/sendNeedsDiscoveryMessage';
import type { extractIdeaFraming } from '../../../methods/src/extractIdeaFraming';
import type { confirmIdeaFraming } from '../../../methods/src/confirmIdeaFraming';
import type { confirmNeedsDiscoveryFraming } from '../../../methods/src/confirmNeedsDiscoveryFraming';
import type { reframeNeedsDiscoverySession } from '../../../methods/src/reframeNeedsDiscoverySession';
import type { ideaFramingImpact } from '../../../methods/src/ideaFramingImpact';
import type { listEarlierNeedsDiscoverySessions } from '../../../methods/src/listEarlierNeedsDiscoverySessions';
import type { startNeedsDiscoveryRound } from '../../../methods/src/startNeedsDiscoveryRound';
import type { getMemoCostPreview } from '../../../methods/src/getMemoCostPreview';
import type { startPatentRound } from '../../../methods/src/startPatentRound';
import type { getIdeaRounds } from '../../../methods/src/getIdeaRounds';
import type { getAdvisorContext } from '../../../methods/src/getAdvisorContext';
import type { getSessionRecord } from '../../../methods/src/getSessionRecord';
import type { exportSessionRecordPdf } from '../../../methods/src/exportSessionRecordPdf';
import type { needsDiscoveryFramingTurn } from '../../../methods/src/needsDiscoveryFramingTurn';
import type {
  finalizeNeedsDiscoverySession,
  FinalizeNeedsDiscoverySessionInput,
} from '../../../methods/src/finalizeNeedsDiscoverySession';
import type {
  getNeedsDiscoveryMemo,
  GetNeedsDiscoveryMemoInput,
} from '../../../methods/src/getNeedsDiscoveryMemo';
import type {
  listNeedsDiscoveryMemos,
  ListNeedsDiscoveryMemosInput,
} from '../../../methods/src/listNeedsDiscoveryMemos';
import type {
  getNeedsDiscoveryMemoPdf,
  GetNeedsDiscoveryMemoPdfInput,
} from '../../../methods/src/getNeedsDiscoveryMemoPdf';
import type { exportMemoPdf, ExportMemoPdfInput } from '../../../methods/src/exportMemoPdf';
import type {
  startBusinessCaseSession,
  StartBusinessCaseSessionInput,
} from '../../../methods/src/startBusinessCaseSession';
import type {
  getBusinessCaseSession,
  GetBusinessCaseSessionInput,
} from '../../../methods/src/getBusinessCaseSession';
import type {
  sendBusinessCaseMessage,
  SendBusinessCaseMessageInput,
} from '../../../methods/src/sendBusinessCaseMessage';
import type {
  finalizeBusinessCaseSession,
  FinalizeBusinessCaseSessionInput,
} from '../../../methods/src/finalizeBusinessCaseSession';
import type {
  getBusinessCaseMemo,
  GetBusinessCaseMemoInput,
} from '../../../methods/src/getBusinessCaseMemo';
import type {
  listBusinessCaseMemos,
  ListBusinessCaseMemosInput,
} from '../../../methods/src/listBusinessCaseMemos';
import type {
  getBusinessCaseMemoPdf,
  GetBusinessCaseMemoPdfInput,
} from '../../../methods/src/getBusinessCaseMemoPdf';
import type { listLearningPaths } from '../../../methods/src/listLearningPaths';
import type { listLearningCurriculums } from '../../../methods/src/listLearningCurriculums';
import type {
  listMarketplacePractitioners,
  ListMarketplacePractitionersInput,
} from '../../../methods/src/listMarketplacePractitioners';
import type { getMyAchievements } from '../../../methods/src/getMyAchievements';
import type { getLearningPath } from '../../../methods/src/getLearningPath';
import type { getLesson } from '../../../methods/src/getLesson';
import type { completeLesson } from '../../../methods/src/completeLesson';
import type { submitQuizAttempt } from '../../../methods/src/submitQuizAttempt';
import type { getDailyInsight } from '../../../methods/src/getDailyInsight';
import type { subscribe, SubscribeInput } from '../../../methods/src/subscribe';
import type { cancelSubscription } from '../../../methods/src/cancelSubscription';
import type { getBillingConfig } from '../../../methods/src/getBillingConfig';
import type {
  createCheckoutSession,
  CreateCheckoutSessionInput,
} from '../../../methods/src/createCheckoutSession';
import type {
  createBillingPortalSession,
  CreateBillingPortalSessionInput,
} from '../../../methods/src/createBillingPortalSession';
import type {
  changeSubscriptionPlan,
  cancelScheduledPlanChange,
  ChangeSubscriptionPlanInput,
} from '../../../methods/src/changeSubscriptionPlan';
import type {
  createOverflowCheckoutSession,
  CreateOverflowCheckoutSessionInput,
} from '../../../methods/src/createOverflowCheckoutSession';
import type {
  createDeepWorkCheckoutSession,
  CreateDeepWorkCheckoutSessionInput,
} from '../../../methods/src/createDeepWorkCheckoutSession';
import type {
  createStrategySessionCheckoutSession,
  CreateStrategySessionCheckoutSessionInput,
} from '../../../methods/src/createStrategySessionCheckoutSession';
import type {
  createCreditPackCheckoutSession,
  CreateCreditPackCheckoutSessionInput,
} from '../../../methods/src/createCreditPackCheckoutSession';
import type {
  createScanPackCheckoutSession,
  CreateScanPackCheckoutSessionInput,
} from '../../../methods/src/createScanPackCheckoutSession';
import type { adminExportHeroCards } from '../../../methods/src/adminExportHeroCards';
import type { joinWaitlist } from '../../../methods/src/joinWaitlist';
import type { getDashboard } from '../../../methods/src/getDashboard';
import type {
  dismissMemoSanityNotice,
  DismissMemoSanityNoticeInput,
} from '../../../methods/src/dismissMemoSanityNotice';
import type {
  dismissAssumptionNotice,
  DismissAssumptionNoticeInput,
} from '../../../methods/src/dismissAssumptionNotice';
import type { dismissExampleNotice } from '../../../methods/src/dismissExampleNotice';
import type { getFirstRunGuide } from '../../../methods/src/getFirstRunGuide';
import type { updateFirstRunGuide } from '../../../methods/src/updateFirstRunGuide';
import type {
  setMemoCurrencyView,
  SetMemoCurrencyViewInput,
} from '../../../methods/src/setMemoCurrencyView';
import type {
  regenerateMyMemo,
  RegenerateMyMemoInput,
} from '../../../methods/src/regenerateMyMemo';
import type {
  generateSearchStrategy,
  GenerateSearchStrategyInput,
} from '../../../methods/src/generateSearchStrategy';
import type {
  getSearchStrategy,
  GetSearchStrategyInput,
} from '../../../methods/src/getSearchStrategy';
import type {
  startSearchReport,
  StartSearchReportInput,
} from '../../../methods/src/startSearchReport';
import type {
  getSearchReport,
  GetSearchReportInput,
} from '../../../methods/src/getSearchReport';
import type {
  getSearchReportPdf,
  GetSearchReportPdfInput,
} from '../../../methods/src/getSearchReportPdf';
import type { listPublishedBlogPosts } from '../../../methods/src/listPublishedBlogPosts';
import type { getMethodologyStats } from '../../../methods/src/getMethodologyStats';
import type {
  getSearchAccessStatus,
  GetSearchAccessStatusInput,
} from '../../../methods/src/getSearchAccessStatus';
import type {
  createSearchPlanCheckoutSession,
  CreateSearchPlanCheckoutSessionInput,
} from '../../../methods/src/createSearchPlanCheckoutSession';
import type {
  createSearchFullCheckoutSession,
  CreateSearchFullCheckoutSessionInput,
} from '../../../methods/src/createSearchFullCheckoutSession';
import type { getStrategyHub } from '../../../methods/src/getStrategyHub';
import type { submitFeedback } from '../../../methods/src/submitFeedback';
import type { listFeatureRequests } from '../../../methods/src/listFeatureRequests';
import type { getFeatureRequest } from '../../../methods/src/getFeatureRequest';
import type { submitFeatureRequest } from '../../../methods/src/submitFeatureRequest';
import type { voteFeatureRequest } from '../../../methods/src/voteFeatureRequest';
import type { commentFeatureRequest } from '../../../methods/src/commentFeatureRequest';
import type { submitSurvey, SubmitSurveyInput } from '../../../methods/src/submitSurvey';
import type { getOnboardingState } from '../../../methods/src/getOnboardingState';
import type { reconnectDuplicateIdentity } from '../../../methods/src/reconnectDuplicateIdentity';
import type { getGuestDemo } from '../../../methods/src/getGuestDemo';
import type { getGuestDemoVerdict } from '../../../methods/src/getGuestDemoVerdict';
import type { adminListUsers } from '../../../methods/src/adminListUsers';
import type { adminListDuplicateAccounts } from '../../../methods/src/adminListDuplicateAccounts';
import type { adminMergeAccounts } from '../../../methods/src/adminMergeAccounts';
import type { adminSendMergeOffer } from '../../../methods/src/adminSendMergeOffer';
import type { getMergeOffer } from '../../../methods/src/getMergeOffer';
import type { checkMergeOfferPhone } from '../../../methods/src/checkMergeOfferPhone';
import type { acceptMergeOffer } from '../../../methods/src/acceptMergeOffer';
import type { adminGetUserDetail } from '../../../methods/src/adminGetUserDetail';
import type { adminListSurveys } from '../../../methods/src/adminListSurveys';
import type { adminListInventionFields } from '../../../methods/src/adminListInventionFields';
import type { getUnservedIndustryDemand } from '../../../methods/src/getUnservedIndustryDemand';
import type { getMonitorFeed } from '../../../methods/src/getMonitorFeed';
import type { getMonitorEntities } from '../../../methods/src/getMonitorEntities';
import type { getMonitorEntityTimeline } from '../../../methods/src/getMonitorEntityTimeline';
import type { markMonitorAlert } from '../../../methods/src/markMonitorAlert';
import type { runMonitorSweep } from '../../../methods/src/runMonitorSweep';
import type {
  adminCreateNeedCluster,
  AdminCreateNeedClusterInput,
} from '../../../methods/src/adminCreateNeedCluster';
import type {
  adminScanNeedCluster,
  AdminScanNeedClusterInput,
} from '../../../methods/src/adminScanNeedCluster';
import type {
  adminManageNeedCluster,
  AdminManageNeedClusterInput,
} from '../../../methods/src/adminManageNeedCluster';
import type {
  adminAddIncubatorProgram,
  AdminAddIncubatorProgramInput,
} from '../../../methods/src/adminAddIncubatorProgram';
import type { adminListIncubatorPrograms } from '../../../methods/src/adminListIncubatorPrograms';
import type {
  adminRemoveIncubatorProgram,
  AdminRemoveIncubatorProgramInput,
} from '../../../methods/src/adminRemoveIncubatorProgram';
import type { adminListSurveyTemplates } from '../../../methods/src/adminListSurveyTemplates';
import type { adminGetSurveyTemplate } from '../../../methods/src/adminGetSurveyTemplate';
import type {
  adminSaveSurveyTemplate,
  AdminSaveSurveyTemplateInput,
} from '../../../methods/src/adminSaveSurveyTemplate';
import type { adminLaunchSurveyTemplate } from '../../../methods/src/adminLaunchSurveyTemplate';
import type { adminRetireSurveyTemplate } from '../../../methods/src/adminRetireSurveyTemplate';
import type {
  getActiveSurveyTemplate,
  GetActiveSurveyTemplateInput,
} from '../../../methods/src/getActiveSurveyTemplate';
import type {
  subscribeNewsletter,
  SubscribeNewsletterInput,
} from '../../../methods/src/subscribeNewsletter';
import type { unsubscribeNewsletter } from '../../../methods/src/unsubscribeNewsletter';
import type { getMyNewsletterStatus } from '../../../methods/src/getMyNewsletterStatus';
import type { adminListNewsletterStats } from '../../../methods/src/adminListNewsletterStats';
import type { adminCreateLead } from '../../../methods/src/adminCreateLead';
import type { adminListLeads } from '../../../methods/src/adminListLeads';
import type { adminUpdateLead } from '../../../methods/src/adminUpdateLead';
import type { adminListFeedback } from '../../../methods/src/adminListFeedback';
import type { adminListMemoIssues } from '../../../methods/src/adminListMemoIssues';
import type { adminListPolicyNotices } from '../../../methods/src/adminListPolicyNotices';
import type { adminSendPolicyNotice } from '../../../methods/src/adminSendPolicyNotice';
import type { adminSeedExampleLibrary } from '../../../methods/src/adminSeedExampleLibrary';
import type { adminListExampleLibrary } from '../../../methods/src/adminListExampleLibrary';
import type { adminRegenerateMemo } from '../../../methods/src/adminRegenerateMemo';
import type { adminNotifyMemoRestored } from '../../../methods/src/adminNotifyMemoRestored';
import type { adminListContentPosts } from '../../../methods/src/adminListContentPosts';
import type { adminListKeywords } from '../../../methods/src/adminListKeywords';
import type { adminReviewKeyword, adminAddKeyword } from '../../../methods/src/adminReviewKeyword';
import type { generateKeywordPostNow } from '../../../methods/src/generateKeywordPostNow';
import type { adminListClusters } from '../../../methods/src/adminListClusters';
import type { adminCreateCluster } from '../../../methods/src/adminCreateCluster';
import type { adminAssignClusterSpokes } from '../../../methods/src/adminAssignClusterSpokes';
import type { adminRemoveClusterSpoke } from '../../../methods/src/adminRemoveClusterSpoke';
import type { adminSetClusterVelocity } from '../../../methods/src/adminSetClusterVelocity';
import type { adminDeleteCluster } from '../../../methods/src/adminDeleteCluster';
import type { adminGenerateClusterContent } from '../../../methods/src/adminGenerateClusterContent';
import type { adminCheckBlogOverlap } from '../../../methods/src/adminCheckBlogOverlap';
import type { getDrawingBrief } from '../../../methods/src/getDrawingBrief';
import type { getSketchWorkspace } from '../../../methods/src/getSketchWorkspace';
import type { getSketchUploadSlot } from '../../../methods/src/getSketchUploadSlot';
import type { startSketchRun } from '../../../methods/src/startSketchRun';
import type { pickSketchCandidate } from '../../../methods/src/pickSketchCandidate';
import type { getSketchDownload } from '../../../methods/src/getSketchDownload';
import type { getDiagramDownload } from '../../../methods/src/getDiagramDownload';
import type { saveSketchAnchors } from '../../../methods/src/saveSketchAnchors';
import type { updateDrawingBriefMeta } from '../../../methods/src/updateDrawingBriefMeta';
import type { draftDrawingBrief } from '../../../methods/src/draftDrawingBrief';
import type { saveDrawingBrief } from '../../../methods/src/saveDrawingBrief';
import type { refineDrawingBrief } from '../../../methods/src/refineDrawingBrief';
import type { listMemoAnnotations } from '../../../methods/src/listMemoAnnotations';
import type { saveMemoAnnotation } from '../../../methods/src/saveMemoAnnotation';
import type { deleteMemoAnnotation } from '../../../methods/src/deleteMemoAnnotation';
import type { checkSessionMessage } from '../../../methods/src/checkSessionMessage';
import type { createMemoShareLink } from '../../../methods/src/createMemoShareLink';
import type { listMemoShareLinks } from '../../../methods/src/listMemoShareLinks';
import type { revokeMemoShareLink } from '../../../methods/src/revokeMemoShareLink';
import type { getSharedMemo } from '../../../methods/src/getSharedMemo';
import type { getSearchStrategyVersion } from '../../../methods/src/getSearchStrategyVersion';
import type { listDocAnnotations } from '../../../methods/src/listDocAnnotations';
import type { saveDocAnnotation } from '../../../methods/src/saveDocAnnotation';
import type { deleteDocAnnotation } from '../../../methods/src/deleteDocAnnotation';
import type { listLessonAnnotations } from '../../../methods/src/listLessonAnnotations';
import type { saveLessonAnnotation } from '../../../methods/src/saveLessonAnnotation';
import type { deleteLessonAnnotation } from '../../../methods/src/deleteLessonAnnotation';
import type { makeAnnotationTask } from '../../../methods/src/makeAnnotationTask';
import type { listNotes } from '../../../methods/src/listNotes';
import type { saveQuickNote } from '../../../methods/src/saveQuickNote';
import type { updateNoteItem } from '../../../methods/src/updateNoteItem';
import type { deleteNoteItem } from '../../../methods/src/deleteNoteItem';
import type { makeNoteTask } from '../../../methods/src/makeNoteTask';
import type { addPlanningTask } from '../../../methods/src/addPlanningTask';
import type { suggestPlanningTasks } from '../../../methods/src/suggestPlanningTasks';
import type { saveCustomerDiscovery } from '../../../methods/src/saveCustomerDiscovery';
import type { refineDiscoveryQuestion } from '../../../methods/src/refineDiscoveryQuestion';
import type { suggestBriefAssembly } from '../../../methods/src/suggestBriefAssembly';
import type { dismissBriefChange } from '../../../methods/src/dismissBriefChange';
import type { confirmDrawingBrief } from '../../../methods/src/confirmDrawingBrief';
import type { keepDrawingBrief } from '../../../methods/src/keepDrawingBrief';
import type { getDrawingBriefPdf } from '../../../methods/src/getDrawingBriefPdf';
import type { listDesignSketches } from '../../../methods/src/listDesignSketches';
import type { createSketchCheckoutSession } from '../../../methods/src/createSketchCheckoutSession';
import type { registerDesignSetInterest } from '../../../methods/src/registerDesignSetInterest';
import type { createDesignSetCheckoutSession } from '../../../methods/src/createDesignSetCheckoutSession';
import type { useDesignSetPack } from '../../../methods/src/useDesignSetPack';
import type { startDrawingDraft } from '../../../methods/src/startDrawingDraft';
import type { getDrawingDraft } from '../../../methods/src/getDrawingDraft';
import type { updateDrawingDraft } from '../../../methods/src/updateDrawingDraft';
import type { setDrawingDraftReminder } from '../../../methods/src/setDrawingDraftReminder';
import type { resumeDrawingDraft } from '../../../methods/src/resumeDrawingDraft';
import type { getGuestUploadSlot } from '../../../methods/src/getGuestUploadSlot';
import type { claimDrawingDraft } from '../../../methods/src/claimDrawingDraft';
import type { listRelatedReading } from '../../../methods/src/listRelatedReading';
import type { adminSeedDrawingsClusters } from '../../../methods/src/adminSeedDrawingsClusters';
import type { adminNotifyDesignSetInterest } from '../../../methods/src/adminNotifyDesignSetInterest';
import type { adminListSketchPurchases } from '../../../methods/src/adminListSketchPurchases';
import type { getDetailDrawings } from '../../../methods/src/getDetailDrawings';
import type { startDetailDrawing } from '../../../methods/src/startDetailDrawing';
import type { getDetailDownload } from '../../../methods/src/getDetailDownload';
import type { saveDetailAnchor } from '../../../methods/src/saveDetailAnchor';
import type { makeDetailCurrent } from '../../../methods/src/makeDetailCurrent';
import type { getConceptModels } from '../../../methods/src/getConceptModels';
import type { startConceptModel } from '../../../methods/src/startConceptModel';
import type { confirmSketchAnchors } from '../../../methods/src/confirmSketchAnchors';
import type { getDrawingFigureSheet } from '../../../methods/src/getDrawingFigureSheet';
import type { buildDrawingFigureSheet } from '../../../methods/src/buildDrawingFigureSheet';
import type { getDrawingFigureSheetDownload } from '../../../methods/src/getDrawingFigureSheetDownload';
import type { createModelAddonCheckoutSession } from '../../../methods/src/createModelAddonCheckoutSession';
import type { suggestRebuildParts } from '../../../methods/src/suggestRebuildParts';
import type { draftFixNote } from '../../../methods/src/draftFixNote';
import type { getModelUploadSlot } from '../../../methods/src/getModelUploadSlot';
import type { saveModelUpload } from '../../../methods/src/saveModelUpload';
import type { proposeModelChecklist } from '../../../methods/src/proposeModelChecklist';
import type { getModelEditor } from '../../../methods/src/getModelEditor';
import type { editConceptModel } from '../../../methods/src/editConceptModel';
import type { getGuidedBuild } from '../../../methods/src/getGuidedBuild';
import type { updateGuidedBuild } from '../../../methods/src/updateGuidedBuild';
import type { previewGuidedChecks } from '../../../methods/src/previewGuidedChecks';
import type { finishGuidedBuild } from '../../../methods/src/finishGuidedBuild';
import type { makeConceptModelCurrent } from '../../../methods/src/makeConceptModelCurrent';
import type { getConceptModelDownload } from '../../../methods/src/getConceptModelDownload';
import type { adminSeedConceptModel } from '../../../methods/src/adminSeedConceptModel';
import type { adminConceptModelStats } from '../../../methods/src/adminConceptModelStats';
import type { buildFigureSheet } from '../../../methods/src/buildFigureSheet';
import type { startRealisticRender } from '../../../methods/src/startRealisticRender';
import type { getDemoWalkthrough } from '../../../methods/src/getDemoWalkthrough';
import type { listLiveDemos } from '../../../methods/src/listLiveDemos';
import type { getPublicDemoStatus } from '../../../methods/src/getPublicDemoStatus';
import type { adminAdvanceDemoWalkthrough } from '../../../methods/src/adminAdvanceDemoWalkthrough';
import type { adminListDemoWalkthroughs } from '../../../methods/src/adminListDemoWalkthroughs';
import type { adminSetDemoWalkthroughLive } from '../../../methods/src/adminSetDemoWalkthroughLive';
import type { getFigureSheetDownload } from '../../../methods/src/getFigureSheetDownload';
import type { createComponentPackCheckoutSession } from '../../../methods/src/createComponentPackCheckoutSession';
import type { createRenderPackCheckoutSession } from '../../../methods/src/createRenderPackCheckoutSession';
import type { adminMatrixList } from '../../../methods/src/adminMatrixList';
import type { adminMatrixPreview } from '../../../methods/src/adminMatrixPreview';
import type { adminMatrixGenerate } from '../../../methods/src/adminMatrixGenerate';
import type { adminMatrixSetCap } from '../../../methods/src/adminMatrixSetCap';
import type { adminListInternalLinkSuggestions } from '../../../methods/src/adminListInternalLinkSuggestions';
import type { adminGenerateInternalLinks } from '../../../methods/src/adminGenerateInternalLinks';
import type { adminResolveInternalLinkSuggestion } from '../../../methods/src/adminResolveInternalLinkSuggestion';
import type { adminRunRetroactiveInternalLinks } from '../../../methods/src/adminRunRetroactiveInternalLinks';
import type { adminListCompetitors } from '../../../methods/src/adminListCompetitors';
import type { adminAddCompetitor, adminReviewCompetitor } from '../../../methods/src/adminManageCompetitors';
import type { adminDiscoverCompetitors } from '../../../methods/src/adminDiscoverCompetitors';
import type { adminRunKeywordEngine } from '../../../methods/src/adminRunKeywordEngine';
import type { generateComparisonDraft } from '../../../methods/src/generateComparisonDraft';
import type { generateDataBriefDraft } from '../../../methods/src/generateDataBriefDraft';
import type { adminApproveDataBrief } from '../../../methods/src/adminApproveDataBrief';
import type { adminReviseComparisonDraft } from '../../../methods/src/adminReviseComparisonDraft';
import type { applyToAffiliate, getMyAffiliate } from '../../../methods/src/applyToAffiliate';
import type { getAffiliateProgramInfo } from '../../../methods/src/getAffiliateProgramInfo';
import type {
  adminListAffiliates,
  adminReviewAffiliate,
  adminUpdateAffiliateSettings,
  adminUpdateAffiliateProgramSettings,
  adminRecordAffiliatePayout,
} from '../../../methods/src/adminAffiliates';
import type {
  adminRunContentBatch,
  adminGetContentBatchDefaults,
  adminSetContentBatchSkip,
} from '../../../methods/src/adminRunContentBatch';
import type { adminListContentBatchRuns } from '../../../methods/src/adminListContentBatchRuns';
import type { adminGetContentPost } from '../../../methods/src/adminGetContentPost';
import type { adminCreateContentPost } from '../../../methods/src/adminCreateContentPost';
import type { adminUpdateContentPost } from '../../../methods/src/adminUpdateContentPost';
import type { adminDeleteContentPost } from '../../../methods/src/adminDeleteContentPost';
import type { adminGetBeehiivStatus } from '../../../methods/src/adminGetBeehiivStatus';
import type { adminPublishPostToBeehiiv } from '../../../methods/src/adminPublishPostToBeehiiv';
import type { adminListVisitors } from '../../../methods/src/adminListVisitors';
import type { adminTrafficSources } from '../../../methods/src/adminTrafficSources';
import type { getManagerBoard } from '../../../methods/src/getManagerBoard';
import type { getManagerAsset } from '../../../methods/src/getManagerAsset';
import type { unlockManagerInvention } from '../../../methods/src/unlockManagerInvention';
import type { updateManagerAsset } from '../../../methods/src/updateManagerAsset';
import type { createManagerCheckoutSession } from '../../../methods/src/createManagerCheckoutSession';
import type { adminManagerOverview } from '../../../methods/src/adminManagerOverview';
import type { getAdminUserSupportPanel } from '../../../methods/src/getAdminUserSupportPanel';
import type { adminSearchUsers } from '../../../methods/src/adminSearchUsers';
import type { grantAdminSessionCredit } from '../../../methods/src/grantAdminSessionCredit';
import type { grantAdminEntitlement } from '../../../methods/src/grantAdminEntitlement';
import type { reverseAdminGrant } from '../../../methods/src/reverseAdminGrant';
import type { getPacket } from '../../../methods/src/getPacket';
import type { createPacketShareLink } from '../../../methods/src/createPacketShareLink';
import type { revokePacketShareLink } from '../../../methods/src/revokePacketShareLink';
import type { getSharedPacket } from '../../../methods/src/getSharedPacket';
import type { openSharedPacket } from '../../../methods/src/openSharedPacket';
import type { savePacketDraft } from '../../../methods/src/savePacketDraft';
import type { buildPacket } from '../../../methods/src/buildPacket';
import type { getPacketDownload } from '../../../methods/src/getPacketDownload';
import type { adminSetMemoPaid } from '../../../methods/src/adminSetMemoPaid';
import type { grantAdminReferralCredit } from '../../../methods/src/grantAdminReferralCredit';
import type { addAdminCsNote } from '../../../methods/src/addAdminCsNote';
import type { setAdminUserFlag } from '../../../methods/src/setAdminUserFlag';
import type { setAdminUserInternal } from '../../../methods/src/setAdminUserInternal';
import type { setTesterPass } from '../../../methods/src/setTesterPass';
import type { generateInfluencers } from '../../../methods/src/generateInfluencers';
import type { getInfluencerRun } from '../../../methods/src/getInfluencerRun';
import type { cancelInfluencerRun } from '../../../methods/src/cancelInfluencerRun';
import type { listInfluencerRuns } from '../../../methods/src/listInfluencerRuns';
import type { getInfluencerPipeline } from '../../../methods/src/getInfluencerPipeline';
import type { updateInfluencerCandidate } from '../../../methods/src/updateInfluencerCandidate';
import type { draftInfluencerOutreach } from '../../../methods/src/draftInfluencerOutreach';
import type { adjustAdminSubscription } from '../../../methods/src/adjustAdminSubscription';
import type { listAdminFlagged } from '../../../methods/src/listAdminFlagged';
import type { adminPreviewCampaignAudience } from '../../../methods/src/adminPreviewCampaignAudience';
import type { adminPreviewCampaignEmail } from '../../../methods/src/adminPreviewCampaignEmail';
import type { adminGenerateCampaignContent } from '../../../methods/src/adminGenerateCampaignContent';
import type { adminSaveCampaign } from '../../../methods/src/adminSaveCampaign';
import type { adminListCampaigns } from '../../../methods/src/adminListCampaigns';
import type { adminListFeaturableContent } from '../../../methods/src/adminListFeaturableContent';
import type { adminGetCampaign } from '../../../methods/src/adminGetCampaign';
import type { adminDeleteCampaign } from '../../../methods/src/adminDeleteCampaign';
import type {
  adminListCampaignContent,
  adminSaveCampaignContent,
  adminDeleteCampaignContent,
} from '../../../methods/src/adminCampaignContent';
import type { adminSendNewsletterWelcomeBacklog } from '../../../methods/src/adminSendNewsletterWelcomeBacklog';
import type { generateSparkDirections } from '../../../methods/src/generateSparkDirections';
import type { checkPatentability } from '../../../methods/src/checkPatentability';
import type { adminRegenerateBlogCover } from '../../../methods/src/adminRegenerateBlogCover';
import type { getPatentabilityAllowance } from '../../../methods/src/getPatentabilityAllowance';
import type { submitSparkLead } from '../../../methods/src/submitSparkLead';
import type { getSparkSession } from '../../../methods/src/getSparkSession';
import type { generateSparkReport } from '../../../methods/src/generateSparkReport';
import type { getSparkWorkspace } from '../../../methods/src/getSparkWorkspace';
import type { listSparkRuns } from '../../../methods/src/listSparkRuns';
import type { sparkRunDirections } from '../../../methods/src/sparkRunDirections';
import type { sparkGenerateMoreDirections } from '../../../methods/src/sparkGenerateMoreDirections';
import type { sparkRunReport } from '../../../methods/src/sparkRunReport';
import type { sparkConvertToIdea } from '../../../methods/src/sparkConvertToIdea';
import type { claimSparkAnonymousSession } from '../../../methods/src/claimSparkAnonymousSession';
import type { createSparkPackCheckoutSession } from '../../../methods/src/createSparkPackCheckoutSession';
import type { adminSendTestEmail } from '../../../methods/src/adminSendTestEmail';
import type { adminSendCampaign } from '../../../methods/src/adminSendCampaign';
import type { adminListLifecycleSegments } from '../../../methods/src/adminListLifecycleSegments';
import type { adminPreviewLifecycleCampaign } from '../../../methods/src/adminPreviewLifecycleCampaign';
import type { adminSendLifecycleCampaign } from '../../../methods/src/adminSendLifecycleCampaign';
import type { adminGetDashboardCore } from '../../../methods/src/adminGetDashboardCore';
import type { adminGetDashboardActivity } from '../../../methods/src/adminGetDashboardActivity';
import type { adminGetSeoHealth, SeoHealthLedgerRow } from '../../../methods/src/adminGetSeoHealth';
import type { adminGetSeoHealthSummary } from '../../../methods/src/adminGetSeoHealthSummary';
import type { adminCheckSeoRoute } from '../../../methods/src/adminCheckSeoRoute';
import type { adminRunSeoHealthSweep } from '../../../methods/src/adminRunSeoHealthSweep';
import type { adminRecheckFailingSeoRoutes } from '../../../methods/src/adminRecheckFailingSeoRoutes';
import type { adminGetCostsMonth } from '../../../methods/src/adminGetCostsMonth';
import type { adminGetCostsTrailing30 } from '../../../methods/src/adminGetCostsTrailing30';
import type { adminGetOperationalCosts } from '../../../methods/src/adminGetOperationalCosts';
import type { adminGetRankTracker } from '../../../methods/src/adminGetRankTracker';
import type { adminManageRankKeywords } from '../../../methods/src/adminManageRankKeywords';
import type { adminRunRankTrackerNow } from '../../../methods/src/adminRunRankTrackerNow';
import type { adminGetRankInsights } from '../../../methods/src/adminGetRankInsights';
import type { adminGenerateKeywordVariations } from '../../../methods/src/adminGenerateKeywordVariations';
import type { adminGetRevenueOverview } from '../../../methods/src/adminGetRevenueOverview';
import type { adminGetRevenueBucketUsers } from '../../../methods/src/adminGetRevenueBucketUsers';
import type { adminGetRevenueReconciliation } from '../../../methods/src/adminGetRevenueReconciliation';
import type { adminGetRevenueCashMonth } from '../../../methods/src/adminGetRevenueCashMonth';
import type { adminBackfillRevenueEvents } from '../../../methods/src/adminBackfillRevenueEvents';
import type { unsubscribeByToken } from '../../../methods/src/unsubscribeByToken';
import type {
  recordAlphaCapHit,
  RecordAlphaCapHitInput,
} from '../../../methods/src/recordAlphaCapHit';
import type {
  recordVisitorActivity,
  RecordVisitorActivityInput,
} from '../../../methods/src/recordVisitorActivity';
import type {
  acknowledgeConfidentiality,
  AcknowledgeConfidentialityInput,
} from '../../../methods/src/acknowledgeConfidentiality';
import type { syncMarketingConsent } from '../../../methods/src/syncMarketingConsent';
import type {
  submitFamilyBundleInquiry,
  SubmitFamilyBundleInquiryInput,
} from '../../../methods/src/submitFamilyBundleInquiry';
import type { adminListFamilyBundleInquiries } from '../../../methods/src/adminListFamilyBundleInquiries';
import type {
  purchaseIdeaCredit,
  PurchaseIdeaCreditInput,
} from '../../../methods/src/purchaseIdeaCredit';

// Invention Vocabulary (glossary) — Layers 2 & 3
import type {
  saveVocabularyTerm,
  SaveVocabularyTermInput,
} from '../../../methods/src/saveVocabularyTerm';
import type {
  removeVocabularyTerm,
  RemoveVocabularyTermInput,
} from '../../../methods/src/removeVocabularyTerm';
import type { listVocabulary } from '../../../methods/src/listVocabulary';
import type {
  listDueStudyCards,
  ListDueStudyCardsInput,
} from '../../../methods/src/listDueStudyCards';
import type {
  gradeStudyCard,
  GradeStudyCardInput,
} from '../../../methods/src/gradeStudyCard';

// My Invention Log — design decomposition (Phase 1)
import type { getDesign, GetDesignInput } from '../../../methods/src/getDesign';
import type { dismissDesignMemoNotice } from '../../../methods/src/dismissDesignMemoNotice';
import type { saveDesign, SaveDesignInput } from '../../../methods/src/saveDesign';
import type { listInventionLog } from '../../../methods/src/listInventionLog';
import type {
  draftDesignDecomposition,
  DraftDesignDecompositionInput,
} from '../../../methods/src/draftDesignDecomposition';
import type { DesignComponent, MaterialOption } from '../../../methods/src/tables/designs';
import type {
  generateCustomerDiscovery,
  GenerateCustomerDiscoveryInput,
} from '../../../methods/src/generateCustomerDiscovery';
import type {
  getCustomerDiscovery,
  GetCustomerDiscoveryInput,
} from '../../../methods/src/getCustomerDiscovery';
import type {
  generateBusinessCanvas,
  GenerateBusinessCanvasInput,
} from '../../../methods/src/generateBusinessCanvas';
import type {
  getBusinessCanvas,
  GetBusinessCanvasInput,
} from '../../../methods/src/getBusinessCanvas';
import type {
  listBusinessCanvasVersions,
  ListBusinessCanvasVersionsInput,
} from '../../../methods/src/listBusinessCanvasVersions';
import type {
  saveBusinessCanvasEdits,
  SaveBusinessCanvasEditsInput,
} from '../../../methods/src/saveBusinessCanvasEdits';
import type {
  restoreBusinessCanvasVersion,
  RestoreBusinessCanvasVersionInput,
} from '../../../methods/src/restoreBusinessCanvasVersion';
import type {
  generateCanvasAsset,
  GenerateCanvasAssetInput,
} from '../../../methods/src/generateCanvasAsset';

import type { getBusinessDevelopment } from '../../../methods/src/getBusinessDevelopment';
import type { createBdOpportunity } from '../../../methods/src/createBdOpportunity';
import type { updateBdOpportunity } from '../../../methods/src/updateBdOpportunity';
import type { archiveBdOpportunity } from '../../../methods/src/archiveBdOpportunity';
import type { addBdOpportunityUpdate } from '../../../methods/src/addBdOpportunityUpdate';
import type { deleteBdOpportunityUpdate } from '../../../methods/src/deleteBdOpportunityUpdate';
import type { updateBdOwner } from '../../../methods/src/updateBdOwner';
import type { createBdOwner } from '../../../methods/src/createBdOwner';
import type { deleteBdOwner } from '../../../methods/src/deleteBdOwner';
import type { getBdFollowUpsDue } from '../../../methods/src/getBdFollowUpsDue';
import type { getMyBusinessDevelopment } from '../../../methods/src/getMyBusinessDevelopment';
import type { createMyBdOpportunity } from '../../../methods/src/createMyBdOpportunity';
import type { updateMyBdOpportunity } from '../../../methods/src/updateMyBdOpportunity';
import type { archiveMyBdOpportunity } from '../../../methods/src/archiveMyBdOpportunity';
import type { addMyBdOpportunityUpdate } from '../../../methods/src/addMyBdOpportunityUpdate';
import type { deleteMyBdOpportunityUpdate } from '../../../methods/src/deleteMyBdOpportunityUpdate';

interface ApiContract {
  // Live Models (market model, scenarios, Excel download packs).
  getMarketModel: (input: Parameters<typeof getMarketModel>[0]) => ReturnType<typeof getMarketModel>;
  getDemoLiveModel: (input: Parameters<typeof getDemoLiveModel>[0]) => ReturnType<typeof getDemoLiveModel>;
  getDemoModelWorkbook: (input: Parameters<typeof getDemoModelWorkbook>[0]) => ReturnType<typeof getDemoModelWorkbook>;
  getBusinessModel: (input: Parameters<typeof getBusinessModel>[0]) => ReturnType<typeof getBusinessModel>;
  requestScenarioReview: (input: Parameters<typeof requestScenarioReview>[0]) => ReturnType<typeof requestScenarioReview>;
  saveModelScenario: (input: Parameters<typeof saveModelScenario>[0]) => ReturnType<typeof saveModelScenario>;
  deleteModelScenario: (input: Parameters<typeof deleteModelScenario>[0]) => ReturnType<typeof deleteModelScenario>;
  setModelScenarioShared: (input: Parameters<typeof setModelScenarioShared>[0]) => ReturnType<typeof setModelScenarioShared>;
  reapplyModelScenario: (input: Parameters<typeof reapplyModelScenario>[0]) => ReturnType<typeof reapplyModelScenario>;
  getWorkbookQuote: (input: Parameters<typeof getWorkbookQuote>[0]) => ReturnType<typeof getWorkbookQuote>;
  downloadMarketWorkbook: (input: Parameters<typeof downloadMarketWorkbook>[0]) => ReturnType<typeof downloadMarketWorkbook>;
  createExcelPackCheckoutSession: (input: Parameters<typeof createExcelPackCheckoutSession>[0]) => ReturnType<typeof createExcelPackCheckoutSession>;
  // Drafting Workbench (Prepare My Filing)
  getDrafting: (input: Parameters<typeof getDrafting>[0]) => ReturnType<typeof getDrafting>;
  exportDraftingDocument: (input: Parameters<typeof exportDraftingDocument>[0]) => ReturnType<typeof exportDraftingDocument>;
  listDrafting: () => ReturnType<typeof listDrafting>;
  createDraftingCheckoutSession: (input: Parameters<typeof createDraftingCheckoutSession>[0]) => ReturnType<typeof createDraftingCheckoutSession>;
  getPrepPlan: () => ReturnType<typeof getPrepPlan>;
  createPrepPlanCheckoutSession: (input: Parameters<typeof createPrepPlanCheckoutSession>[0]) => ReturnType<typeof createPrepPlanCheckoutSession>;
  setDraftingOffice: (input: Parameters<typeof setDraftingOffice>[0]) => ReturnType<typeof setDraftingOffice>;
  draftDraftingSection: (input: Parameters<typeof draftDraftingSection>[0], options?: { stream?: boolean; onToken?: (text: string) => void; onStreamError?: (err: string) => void }) => ReturnType<typeof draftDraftingSection>;
  refineDraftingText: (input: Parameters<typeof refineDraftingText>[0], options?: { stream?: boolean; onToken?: (text: string) => void; onStreamError?: (err: string) => void }) => ReturnType<typeof refineDraftingText>;
  saveDraftingVersion: (input: Parameters<typeof saveDraftingVersion>[0]) => ReturnType<typeof saveDraftingVersion>;
  updateDraftingVersion: (input: Parameters<typeof updateDraftingVersion>[0]) => ReturnType<typeof updateDraftingVersion>;
  pickDraftingVersion: (input: Parameters<typeof pickDraftingVersion>[0]) => ReturnType<typeof pickDraftingVersion>;
  acknowledgeDraftingResponsibility: (input: Parameters<typeof acknowledgeDraftingResponsibility>[0]) => ReturnType<typeof acknowledgeDraftingResponsibility>;
  getDraftingFigureUploadSlot: (input: Parameters<typeof getDraftingFigureUploadSlot>[0]) => ReturnType<typeof getDraftingFigureUploadSlot>;
  addDraftingFigure: (input: Parameters<typeof addDraftingFigure>[0]) => ReturnType<typeof addDraftingFigure>;
  importDraftingFigures: (input: Parameters<typeof importDraftingFigures>[0]) => ReturnType<typeof importDraftingFigures>;
  saveDraftingFigures: (input: Parameters<typeof saveDraftingFigures>[0]) => ReturnType<typeof saveDraftingFigures>;
  saveDraftingFigureVersion: (input: Parameters<typeof saveDraftingFigureVersion>[0]) => ReturnType<typeof saveDraftingFigureVersion>;
  reviewDraftingFigure: (input: Parameters<typeof reviewDraftingFigure>[0]) => ReturnType<typeof reviewDraftingFigure>;
  redrawDraftingFigure: (input: Parameters<typeof redrawDraftingFigure>[0]) => ReturnType<typeof redrawDraftingFigure>;
  saveDraftingWorking: (input: Parameters<typeof saveDraftingWorking>[0]) => ReturnType<typeof saveDraftingWorking>;
  seedDraftingClaims: (input: Parameters<typeof seedDraftingClaims>[0]) => ReturnType<typeof seedDraftingClaims>;
  saveDraftingClaims: (input: Parameters<typeof saveDraftingClaims>[0]) => ReturnType<typeof saveDraftingClaims>;
  addDraftingClaim: (input: Parameters<typeof addDraftingClaim>[0]) => ReturnType<typeof addDraftingClaim>;
  checkDraftingClaim: (input: Parameters<typeof checkDraftingClaim>[0]) => ReturnType<typeof checkDraftingClaim>;
  runDraftingCheck: (input: Parameters<typeof runDraftingCheck>[0]) => ReturnType<typeof runDraftingCheck>;
  saveDraftingDetails: (input: Parameters<typeof saveDraftingDetails>[0]) => ReturnType<typeof saveDraftingDetails>;
  // Pathway Navigator
  getPathway: (input: Parameters<typeof getPathway>[0]) => ReturnType<typeof getPathway>;
  savePathwayAnswers: (input: Parameters<typeof savePathwayAnswers>[0]) => ReturnType<typeof savePathwayAnswers>;
  startPathwayStrategy: (input: Parameters<typeof startPathwayStrategy>[0]) => ReturnType<typeof startPathwayStrategy>;
  askPathwayStrategist: (input: Parameters<typeof askPathwayStrategist>[0]) => ReturnType<typeof askPathwayStrategist>;
  createPathwayCheckoutSession: (input: Parameters<typeof createPathwayCheckoutSession>[0]) => ReturnType<typeof createPathwayCheckoutSession>;
  exportPathwayPdf: (input: Parameters<typeof exportPathwayPdf>[0]) => ReturnType<typeof exportPathwayPdf>;
  listPathway: () => ReturnType<typeof listPathway>;
  adminGetPathwayFacts: () => ReturnType<typeof adminGetPathwayFacts>;
  adminReviewPathwayChange: (input: Parameters<typeof adminReviewPathwayChange>[0]) => ReturnType<typeof adminReviewPathwayChange>;
  adminUpdatePathwayFact: (input: Parameters<typeof adminUpdatePathwayFact>[0]) => ReturnType<typeof adminUpdatePathwayFact>;
  adminRunPathwayWatch: (input: Parameters<typeof adminRunPathwayWatch>[0]) => ReturnType<typeof adminRunPathwayWatch>;
  adminGetHardwareRules: () => ReturnType<typeof adminGetHardwareRules>;
  adminReviewHardwareChange: (input: Parameters<typeof adminReviewHardwareChange>[0]) => ReturnType<typeof adminReviewHardwareChange>;
  adminReviewHardwareTerm: (input: Parameters<typeof adminReviewHardwareTerm>[0]) => ReturnType<typeof adminReviewHardwareTerm>;
  adminApproveVerifiedHardware: (input?: Parameters<typeof adminApproveVerifiedHardware>[0]) => ReturnType<typeof adminApproveVerifiedHardware>;
  adminRunHardwareRules: (input: Parameters<typeof adminRunHardwareRules>[0]) => ReturnType<typeof adminRunHardwareRules>;
  updateHardwareProfile: (input: Parameters<typeof updateHardwareProfile>[0]) => ReturnType<typeof updateHardwareProfile>;
  refreshHardwareChecklist: (input: Parameters<typeof refreshHardwareChecklist>[0]) => ReturnType<typeof refreshHardwareChecklist>;
  verifyInventionLog: (input: { ideaId: string }) => ReturnType<typeof verifyInventionLog>;
  checkLogRecord: (input: { docId: string; code: string }) => ReturnType<typeof checkLogRecord>;
  createWitnessRequest: (input: Parameters<typeof createWitnessRequest>[0]) => ReturnType<typeof createWitnessRequest>;
  revokeWitnessRequest: (input: Parameters<typeof revokeWitnessRequest>[0]) => ReturnType<typeof revokeWitnessRequest>;
  getWitnessRequest: (input: Parameters<typeof getWitnessRequest>[0]) => ReturnType<typeof getWitnessRequest>;
  signWitnessRequest: (input: Parameters<typeof signWitnessRequest>[0]) => ReturnType<typeof signWitnessRequest>;
  confirmWitnessSignature: (input: Parameters<typeof confirmWitnessSignature>[0]) => ReturnType<typeof confirmWitnessSignature>;
  issueLogPrint: (input: { ideaId: string; entryIds: string[]; autoIds?: string[]; timeZone?: string; includeNarrative?: boolean }) => ReturnType<typeof issueLogPrint>;
  startConceptionNarrative: (input: { ideaId: string }) => ReturnType<typeof startConceptionNarrative>;
  saveDesignVersion: (input: { ideaId: string; name?: string }) => ReturnType<typeof saveDesignVersion>;
  listDesignVersions: (input: { ideaId: string }) => ReturnType<typeof listDesignVersions>;
  restoreDesignVersion: (input: { ideaId: string; versionNumber: number }) => ReturnType<typeof restoreDesignVersion>;
  getInventionLog: (input: { ideaId: string }) => ReturnType<typeof getInventionLog>;
  addLogEntry: (input: { ideaId: string } & LogEntryInput) => ReturnType<typeof addLogEntry>;
  updateLogEntry: (input: { ideaId: string; entryId: string } & LogEntryInput) => ReturnType<typeof updateLogEntry>;
  removeLogEntry: (input: { ideaId: string; entryId: string }) => ReturnType<typeof removeLogEntry>;
  getLogUploadSlot: (input: { ideaId: string; contentType: string; filename?: string }) => ReturnType<typeof getLogUploadSlot>;
  exportInventionLogPdf: (input: { ideaId: string; entryIds: string[]; autoIds?: string[]; witness?: boolean; timeZone?: string; includeNarrative?: boolean }) => ReturnType<typeof exportInventionLogPdf>;
  getMe: (input?: Parameters<typeof getMe>[0]) => ReturnType<typeof getMe>;
  // Business Development pipeline (admin)
  getBusinessDevelopment: () => ReturnType<typeof getBusinessDevelopment>;
  createBdOpportunity: (input: Parameters<typeof createBdOpportunity>[0]) => ReturnType<typeof createBdOpportunity>;
  updateBdOpportunity: (input: Parameters<typeof updateBdOpportunity>[0]) => ReturnType<typeof updateBdOpportunity>;
  archiveBdOpportunity: (input: Parameters<typeof archiveBdOpportunity>[0]) => ReturnType<typeof archiveBdOpportunity>;
  addBdOpportunityUpdate: (input: Parameters<typeof addBdOpportunityUpdate>[0]) => ReturnType<typeof addBdOpportunityUpdate>;
  deleteBdOpportunityUpdate: (input: Parameters<typeof deleteBdOpportunityUpdate>[0]) => ReturnType<typeof deleteBdOpportunityUpdate>;
  updateBdOwner: (input: Parameters<typeof updateBdOwner>[0]) => ReturnType<typeof updateBdOwner>;
  createBdOwner: (input: Parameters<typeof createBdOwner>[0]) => ReturnType<typeof createBdOwner>;
  deleteBdOwner: (input: Parameters<typeof deleteBdOwner>[0]) => ReturnType<typeof deleteBdOwner>;
  // BD — admin follow-up popup (whole board) + teammate self-serve board
  getBdFollowUpsDue: () => ReturnType<typeof getBdFollowUpsDue>;
  getMyBusinessDevelopment: () => ReturnType<typeof getMyBusinessDevelopment>;
  createMyBdOpportunity: (input: Parameters<typeof createMyBdOpportunity>[0]) => ReturnType<typeof createMyBdOpportunity>;
  updateMyBdOpportunity: (input: Parameters<typeof updateMyBdOpportunity>[0]) => ReturnType<typeof updateMyBdOpportunity>;
  archiveMyBdOpportunity: (input: Parameters<typeof archiveMyBdOpportunity>[0]) => ReturnType<typeof archiveMyBdOpportunity>;
  addMyBdOpportunityUpdate: (input: Parameters<typeof addMyBdOpportunityUpdate>[0]) => ReturnType<typeof addMyBdOpportunityUpdate>;
  deleteMyBdOpportunityUpdate: (input: Parameters<typeof deleteMyBdOpportunityUpdate>[0]) => ReturnType<typeof deleteMyBdOpportunityUpdate>;
  getTimesheetContext: () => ReturnType<typeof getTimesheetContext>;
  getMyTimesheetMonth: (input: { month?: string }) => ReturnType<typeof getMyTimesheetMonth>;
  consultantSaveTimeEntry: (input: Parameters<typeof consultantSaveTimeEntry>[0]) => ReturnType<typeof consultantSaveTimeEntry>;
  consultantDeleteTimeEntry: (input: { id: string }) => ReturnType<typeof consultantDeleteTimeEntry>;
  consultantSaveMyDetails: (input: Parameters<typeof consultantSaveMyDetails>[0]) => ReturnType<typeof consultantSaveMyDetails>;
  adminAddConsultant: (input: Parameters<typeof adminAddConsultant>[0]) => ReturnType<typeof adminAddConsultant>;
  adminResendConsultantInvite: (input: { consultantId: string }) => ReturnType<typeof adminResendConsultantInvite>;
  adminListConsultants: () => ReturnType<typeof adminListConsultants>;
  adminGetConsultant: (input: { consultantId: string; month?: string }) => ReturnType<typeof adminGetConsultant>;
  adminUpdateConsultant: (input: Parameters<typeof adminUpdateConsultant>[0]) => ReturnType<typeof adminUpdateConsultant>;
  adminDeleteConsultant: (input: { consultantId: string }) => ReturnType<typeof adminDeleteConsultant>;
  adminSaveTimeEntry: (input: Parameters<typeof adminSaveTimeEntry>[0]) => ReturnType<typeof adminSaveTimeEntry>;
  adminDeleteTimeEntry: (input: { id: string }) => ReturnType<typeof adminDeleteTimeEntry>;
  adminSaveWorkCategory: (input: Parameters<typeof adminSaveWorkCategory>[0]) => ReturnType<typeof adminSaveWorkCategory>;
  adminSaveEvaluation: (input: Parameters<typeof adminSaveEvaluation>[0]) => ReturnType<typeof adminSaveEvaluation>;
  adminDeleteEvaluation: (input: { id: string }) => ReturnType<typeof adminDeleteEvaluation>;
  completeProfile: (input: CompleteProfileInput) => ReturnType<typeof completeProfile>;
  // Referral program: the current user's code, link data, counts, and balance.
  getReferralInfo: () => ReturnType<typeof getReferralInfo>;
  getActiveAnnouncement: () => ReturnType<typeof getActiveAnnouncement>;
  dismissAnnouncement: (input: { announcementId: string }) => ReturnType<typeof dismissAnnouncement>;
  pingAnnouncementClick: (input: { announcementId: string }) => ReturnType<typeof pingAnnouncementClick>;
  adminListAnnouncements: () => ReturnType<typeof adminListAnnouncements>;
  adminSaveAnnouncement: (input: Parameters<typeof adminSaveAnnouncement>[0]) => ReturnType<typeof adminSaveAnnouncement>;
  adminDraftAnnouncement: (input: Parameters<typeof adminDraftAnnouncement>[0]) => ReturnType<typeof adminDraftAnnouncement>;
  adminSetAnnouncementStatus: (input: Parameters<typeof adminSetAnnouncementStatus>[0]) => ReturnType<typeof adminSetAnnouncementStatus>;
  adminDeleteAnnouncement: (input: { id: string }) => ReturnType<typeof adminDeleteAnnouncement>;
  adminPreviewAnnouncementAudience: (input: Parameters<typeof adminPreviewAnnouncementAudience>[0]) => ReturnType<typeof adminPreviewAnnouncementAudience>;
  adminGetAnnouncementEngagement: (input: { announcementId: string }) => ReturnType<typeof adminGetAnnouncementEngagement>;
  updateProfile: (input: UpdateProfileInput) => ReturnType<typeof updateProfile>;
  exportUserData: () => ReturnType<typeof exportUserData>;
  deleteAccount: () => ReturnType<typeof deleteAccount>;
  createIdea: (input: CreateIdeaInput) => ReturnType<typeof createIdea>;
  // Smart Capture: structures a freeform description into form fields.
  structureIdeaDraft: (input: StructureIdeaDraftInput) => ReturnType<typeof structureIdeaDraft>;
  listIdeas: () => ReturnType<typeof listIdeas>;
  getIdea: (input: GetIdeaInput) => ReturnType<typeof getIdea>;
  getIdeaCommandCenter: (input: Parameters<typeof getIdeaCommandCenter>[0]) => ReturnType<typeof getIdeaCommandCenter>;
  getIdeaWorkspace: (input: { ideaId: string }) => ReturnType<typeof getIdeaWorkspace>;
  getIntelligenceCenter: () => ReturnType<typeof getIntelligenceCenter>;
  // Per-idea live Signal feeds (Intelligence Center).
  getIdeaSignals: (input: { ideaId: string }) => ReturnType<typeof getIdeaSignals>;
  getIdeaIncubatorMatches: (input: { ideaId: string }) => ReturnType<typeof getIdeaIncubatorMatches>;
  // Portfolio-level Signals Monitoring hub (standalone /signals).
  getSignalsPortfolio: () => ReturnType<typeof getSignalsPortfolio>;
  // R&D Expense Recorder (/admin/rd) — admin-only.
  getRdLedger: () => ReturnType<typeof getRdLedger>;
  rdClockIn: () => ReturnType<typeof rdClockIn>;
  rdClockOut: (input: { note?: string }) => ReturnType<typeof rdClockOut>;
  rdSaveTimeEntry: (input: {
    id?: string;
    startedAt: number;
    endedAt: number;
    note?: string;
    hourlyRateCents?: number;
  }) => ReturnType<typeof rdSaveTimeEntry>;
  rdDeleteTimeEntry: (input: { id: string }) => ReturnType<typeof rdDeleteTimeEntry>;
  rdSetHourlyRate: (input: { hourlyRateCents: number }) => ReturnType<typeof rdSetHourlyRate>;
  rdSaveExpense: (input: {
    id?: string;
    incurredAt: number;
    category: string;
    amountCents: number;
    description: string;
  }) => ReturnType<typeof rdSaveExpense>;
  rdDeleteExpense: (input: { id: string }) => ReturnType<typeof rdDeleteExpense>;
  rdSavePlatformCost: (input: {
    monthKey: string;
    buildCostCents: number;
    runCostCents: number;
  }) => ReturnType<typeof rdSavePlatformCost>;
  rdDeletePlatformCost: (input: { monthKey: string }) => ReturnType<typeof rdDeletePlatformCost>;
  proposeIdeaWatchlist: (input: { ideaId: string }) => ReturnType<typeof proposeIdeaWatchlist>;
  approveIdeaWatchlist: (input: ApproveIdeaWatchlistInput) => ReturnType<typeof approveIdeaWatchlist>;
  updateSignalCadence: (input: {
    ideaId: string;
    signal: 'demand' | 'competition' | 'news' | 'patent' | 'market' | 'fundraising' | 'ma' | 'emerging';
    cadence: 'off' | 'quarterly' | 'monthly' | 'biweekly' | 'weekly' | 'daily';
  }) => ReturnType<typeof updateSignalCadence>;
  refreshIdeaSignal: (input: {
    ideaId: string;
    signal: 'demand' | 'competition' | 'news' | 'patent' | 'market' | 'fundraising' | 'ma' | 'emerging';
  }) => ReturnType<typeof refreshIdeaSignal>;
  startCategorySignal: (input: {
    ideaId: string;
    signal: 'market' | 'fundraising' | 'ma';
  }) => ReturnType<typeof startCategorySignal>;
  startEmergingSignal: (input: { ideaId: string }) => ReturnType<typeof startEmergingSignal>;
  promoteEmergingCompetitor: (input: { candidateId: string }) => ReturnType<typeof promoteEmergingCompetitor>;
  dismissEmergingCompetitor: (input: { candidateId: string }) => ReturnType<typeof dismissEmergingCompetitor>;
  updateSignalWatch: (input: UpdateSignalWatchInput) => ReturnType<typeof updateSignalWatch>;
  markSignalSeen: (input: {
    ideaId: string;
    signal: 'demand' | 'competition' | 'news' | 'patent' | 'market' | 'fundraising' | 'ma' | 'emerging';
  }) => ReturnType<typeof markSignalSeen>;
  recordNewsStoryOpen: (input: { ideaId: string; link: string; from?: 'brief' | 'news' }) => ReturnType<typeof recordNewsStoryOpen>;
  updateIdea: (input: UpdateIdeaInput) => ReturnType<typeof updateIdea>;
  deleteIdea: (input: DeleteIdeaInput) => ReturnType<typeof deleteIdea>;
  // Idea tags: a personal organization layer read by the shared tag store.
  getIdeaTags: () => ReturnType<typeof getIdeaTags>;
  setIdeaTags: (input: SetIdeaTagsInput) => ReturnType<typeof setIdeaTags>;
  renameIdeaTag: (input: RenameIdeaTagInput) => ReturnType<typeof renameIdeaTag>;
  deleteIdeaTag: (input: DeleteIdeaTagInput) => ReturnType<typeof deleteIdeaTag>;
  // Collections: pinned tags in the sidebar.
  pinCollection: (input: PinCollectionInput) => ReturnType<typeof pinCollection>;
  unpinCollection: (input: UnpinCollectionInput) => ReturnType<typeof unpinCollection>;
  reorderCollections: (input: ReorderCollectionsInput) => ReturnType<typeof reorderCollections>;
  // Focus pin: float an idea to the top of the lists. No version snapshot.
  setIdeaFocus: (input: SetIdeaFocusInput) => ReturnType<typeof setIdeaFocus>;
  setIdeaMarketLens: (input: SetIdeaMarketLensInput) => ReturnType<typeof setIdeaMarketLens>;
  listIdeaVersions: (input: ListIdeaVersionsInput) => ReturnType<typeof listIdeaVersions>;
  chatTurn: (input: ChatTurnInput) => ReturnType<typeof chatTurn>;
  needsDiscoveryChatTurn: (
    input: NeedsDiscoveryChatTurnInput,
    options?: { stream?: boolean; onToken?: (text: string) => void; onStreamError?: (err: string) => void },
  ) => ReturnType<typeof needsDiscoveryChatTurn>;
  claimAnonymousSession: (input: ClaimAnonymousSessionInput) => ReturnType<typeof claimAnonymousSession>;
  claimNeedsDiscoveryAnonymousSession: (input: ClaimNeedsDiscoveryAnonymousSessionInput) => ReturnType<typeof claimNeedsDiscoveryAnonymousSession>;
  startSession: (input: StartSessionInput) => ReturnType<typeof startSession>;
  getSession: (input: GetSessionInput) => ReturnType<typeof getSession>;
  sendSessionMessage: (
    input: SendSessionMessageInput,
    options?: { stream?: boolean; onToken?: (text: string) => void; onStreamError?: (err: string) => void },
  ) => ReturnType<typeof sendSessionMessage>;
  setSessionTone: (input: SetSessionToneInput) => ReturnType<typeof setSessionTone>;
  setSessionRegister: (input: SetSessionRegisterInput) => ReturnType<typeof setSessionRegister>;
  refreshSessionRegister: (
    input: Parameters<typeof refreshSessionRegister>[0],
  ) => ReturnType<typeof refreshSessionRegister>;
  finalizeSession: (input: FinalizeSessionInput) => ReturnType<typeof finalizeSession>;
  getMemo: (input: GetMemoInput) => ReturnType<typeof getMemo>;
  listMemos: (input: ListMemosInput) => ReturnType<typeof listMemos>;
  getMemoPdf: (input: GetMemoPdfInput) => ReturnType<typeof getMemoPdf>;
  submitMemoRating: (input: SubmitMemoRatingInput) => ReturnType<typeof submitMemoRating>;
  getMyMemoRating: (input: GetMyMemoRatingInput) => ReturnType<typeof getMyMemoRating>;
  getPlanningBoard: (input: GetPlanningBoardInput) => ReturnType<typeof getPlanningBoard>;
  listPlanningTasks: () => ReturnType<typeof listPlanningTasks>;
  updateTaskPriority: (input: Parameters<typeof updateTaskPriority>[0]) => ReturnType<typeof updateTaskPriority>;
  canvasAssist: (input: Parameters<typeof canvasAssist>[0]) => ReturnType<typeof canvasAssist>;
  setCanvasObjective: (input: Parameters<typeof setCanvasObjective>[0]) => ReturnType<typeof setCanvasObjective>;
  refreshTasksFromMemos: (input: RefreshTasksFromMemosInput) => ReturnType<typeof refreshTasksFromMemos>;
  toggleTaskComplete: (input: ToggleTaskCompleteInput) => ReturnType<typeof toggleTaskComplete>;
  updateTaskDeadline: (input: UpdateTaskDeadlineInput) => ReturnType<typeof updateTaskDeadline>;
  updateTaskNotes: (input: UpdateTaskNotesInput) => ReturnType<typeof updateTaskNotes>;
  getProductRating: (input?: Record<string, never>) => ReturnType<typeof getProductRating>;
  startGoogleConnect: (input?: Record<string, never>) => ReturnType<typeof startGoogleConnect>;
  completeGoogleConnect: (input: CompleteGoogleConnectInput) => ReturnType<typeof completeGoogleConnect>;
  getConnectedAccounts: (input?: Record<string, never>) => ReturnType<typeof getConnectedAccounts>;
  getCalendarFeed: (input: Parameters<typeof getCalendarFeed>[0]) => ReturnType<typeof getCalendarFeed>;
  revokeConnectedAccount: (input: RevokeConnectedAccountInput) => ReturnType<typeof revokeConnectedAccount>;
  exportMemoToGoogleDocs: (input: ExportMemoToGoogleDocsInput) => ReturnType<typeof exportMemoToGoogleDocs>;
  exportMemoToOneDrive: (input: ExportMemoToOneDriveInput) => ReturnType<typeof exportMemoToOneDrive>;
  detectMailProvider: (input: DetectMailProviderInput) => ReturnType<typeof detectMailProvider>;
  saveDashboardPrefs: (input: SaveDashboardPrefsInput) => ReturnType<typeof saveDashboardPrefs>;
  startMicrosoftConnect: (input: StartMicrosoftConnectInput) => ReturnType<typeof startMicrosoftConnect>;
  completeMicrosoftConnect: (input: CompleteMicrosoftConnectInput) => ReturnType<typeof completeMicrosoftConnect>;
  startNotionConnect: (input?: Record<string, never>) => ReturnType<typeof startNotionConnect>;
  completeNotionConnect: (input: CompleteNotionConnectInput) => ReturnType<typeof completeNotionConnect>;
  listNotionDestinations: (input: ListNotionDestinationsInput) => ReturnType<typeof listNotionDestinations>;
  sendMemoToNotion: (input: SendMemoToNotionInput) => ReturnType<typeof sendMemoToNotion>;
  getNotionExport: (input: { exportId: string }) => ReturnType<typeof getNotionExport>;

  // Keeping sessions about their own idea (different-idea check, memo check).
  captureDifferentIdea: (input: CaptureDifferentIdeaInput) => ReturnType<typeof captureDifferentIdea>;
  moveExampleConversation: (input: MoveExampleConversationInput) => ReturnType<typeof moveExampleConversation>;

  // Needs Discovery (Product Strategist, Stage 1)
  startNeedsDiscoverySession: (
    input: StartNeedsDiscoverySessionInput,
  ) => ReturnType<typeof startNeedsDiscoverySession>;
  getNeedsDiscoverySession: (
    input: GetNeedsDiscoverySessionInput,
  ) => ReturnType<typeof getNeedsDiscoverySession>;
  extractIdeaFraming: (input: Parameters<typeof extractIdeaFraming>[0]) => ReturnType<typeof extractIdeaFraming>;
  confirmIdeaFraming: (input: Parameters<typeof confirmIdeaFraming>[0]) => ReturnType<typeof confirmIdeaFraming>;
  confirmNeedsDiscoveryFraming: (input: Parameters<typeof confirmNeedsDiscoveryFraming>[0]) => ReturnType<typeof confirmNeedsDiscoveryFraming>;
  reframeNeedsDiscoverySession: (input: Parameters<typeof reframeNeedsDiscoverySession>[0]) => ReturnType<typeof reframeNeedsDiscoverySession>;
  ideaFramingImpact: (input: Parameters<typeof ideaFramingImpact>[0]) => ReturnType<typeof ideaFramingImpact>;
  listEarlierNeedsDiscoverySessions: (input: Parameters<typeof listEarlierNeedsDiscoverySessions>[0]) => ReturnType<typeof listEarlierNeedsDiscoverySessions>;
  startNeedsDiscoveryRound: (input: Parameters<typeof startNeedsDiscoveryRound>[0]) => ReturnType<typeof startNeedsDiscoveryRound>;
  getMemoCostPreview: (input: Parameters<typeof getMemoCostPreview>[0]) => ReturnType<typeof getMemoCostPreview>;
  startPatentRound: (input: Parameters<typeof startPatentRound>[0]) => ReturnType<typeof startPatentRound>;
  getIdeaRounds: (input: Parameters<typeof getIdeaRounds>[0]) => ReturnType<typeof getIdeaRounds>;
  getAdvisorContext: (input: Parameters<typeof getAdvisorContext>[0]) => ReturnType<typeof getAdvisorContext>;
  getSessionRecord: (input: Parameters<typeof getSessionRecord>[0]) => ReturnType<typeof getSessionRecord>;
  exportSessionRecordPdf: (input: Parameters<typeof exportSessionRecordPdf>[0]) => ReturnType<typeof exportSessionRecordPdf>;
  needsDiscoveryFramingTurn: (input: Parameters<typeof needsDiscoveryFramingTurn>[0]) => ReturnType<typeof needsDiscoveryFramingTurn>;
  sendNeedsDiscoveryMessage: (
    input: SendNeedsDiscoveryMessageInput,
    options?: { stream?: boolean; onToken?: (text: string) => void; onStreamError?: (err: string) => void },
  ) => ReturnType<typeof sendNeedsDiscoveryMessage>;
  finalizeNeedsDiscoverySession: (
    input: FinalizeNeedsDiscoverySessionInput,
  ) => ReturnType<typeof finalizeNeedsDiscoverySession>;
  getNeedsDiscoveryMemo: (
    input: GetNeedsDiscoveryMemoInput,
  ) => ReturnType<typeof getNeedsDiscoveryMemo>;
  listNeedsDiscoveryMemos: (
    input: ListNeedsDiscoveryMemosInput,
  ) => ReturnType<typeof listNeedsDiscoveryMemos>;
  getNeedsDiscoveryMemoPdf: (
    input: GetNeedsDiscoveryMemoPdfInput,
  ) => ReturnType<typeof getNeedsDiscoveryMemoPdf>;
  /** All three advisor memos: printed from the on-screen memo, with a server-drawn cover. */
  exportMemoPdf: (input: ExportMemoPdfInput) => ReturnType<typeof exportMemoPdf>;

  // Business Case (Financial Analyst, Stage 3)
  startBusinessCaseSession: (
    input: StartBusinessCaseSessionInput,
  ) => ReturnType<typeof startBusinessCaseSession>;
  getBusinessCaseSession: (
    input: GetBusinessCaseSessionInput,
  ) => ReturnType<typeof getBusinessCaseSession>;
  sendBusinessCaseMessage: (
    input: SendBusinessCaseMessageInput,
    options?: { stream?: boolean; onToken?: (text: string) => void; onStreamError?: (err: string) => void },
  ) => ReturnType<typeof sendBusinessCaseMessage>;
  finalizeBusinessCaseSession: (
    input: FinalizeBusinessCaseSessionInput,
  ) => ReturnType<typeof finalizeBusinessCaseSession>;
  getBusinessCaseMemo: (
    input: GetBusinessCaseMemoInput,
  ) => ReturnType<typeof getBusinessCaseMemo>;
  listBusinessCaseMemos: (
    input: ListBusinessCaseMemosInput,
  ) => ReturnType<typeof listBusinessCaseMemos>;
  getBusinessCaseMemoPdf: (
    input: GetBusinessCaseMemoPdfInput,
  ) => ReturnType<typeof getBusinessCaseMemoPdf>;
  listLearningPaths: () => ReturnType<typeof listLearningPaths>;
  listLearningCurriculums: () => ReturnType<typeof listLearningCurriculums>;
  listMarketplacePractitioners: (
    input?: ListMarketplacePractitionersInput,
  ) => ReturnType<typeof listMarketplacePractitioners>;
  getMyAchievements: () => ReturnType<typeof getMyAchievements>;
  getLearningPath: (input: GetLearningPathInput) => ReturnType<typeof getLearningPath>;
  getLesson: (input: GetLessonInput) => ReturnType<typeof getLesson>;
  completeLesson: (input: CompleteLessonInput) => ReturnType<typeof completeLesson>;
  submitQuizAttempt: (input: SubmitQuizAttemptInput) => ReturnType<typeof submitQuizAttempt>;
  getDailyInsight: () => ReturnType<typeof getDailyInsight>;
  subscribe: (input?: SubscribeInput) => ReturnType<typeof subscribe>;
  cancelSubscription: () => ReturnType<typeof cancelSubscription>;

  // Real billing (Stripe) — live behind the BILLING_ENABLED launch flag.
  // `getBillingConfig` tells the frontend which path to render (live Checkout
  // vs. the placeholder). The three session creators return a hosted Stripe
  // URL the frontend opens with `window.open(url, '_top')`.
  getBillingConfig: () => ReturnType<typeof getBillingConfig>;
  createCheckoutSession: (
    input: CreateCheckoutSessionInput,
  ) => ReturnType<typeof createCheckoutSession>;
  createBillingPortalSession: (
    input: CreateBillingPortalSessionInput,
  ) => ReturnType<typeof createBillingPortalSession>;
  // Change an existing subscriber's plan in place (upgrade/downgrade with
  // proration) — never creates a second subscription.
  changeSubscriptionPlan: (
    input: ChangeSubscriptionPlanInput,
  ) => ReturnType<typeof changeSubscriptionPlan>;
  // Call off a queued downgrade or cancellation, leaving the plan as-is.
  cancelScheduledPlanChange: () => ReturnType<typeof cancelScheduledPlanChange>;
  createOverflowCheckoutSession: (
    input: CreateOverflowCheckoutSessionInput,
  ) => ReturnType<typeof createOverflowCheckoutSession>;
  createDeepWorkCheckoutSession: (
    input: CreateDeepWorkCheckoutSessionInput,
  ) => ReturnType<typeof createDeepWorkCheckoutSession>;
  createStrategySessionCheckoutSession: (
    input: CreateStrategySessionCheckoutSessionInput,
  ) => ReturnType<typeof createStrategySessionCheckoutSession>;
  createCreditPackCheckoutSession: (
    input: CreateCreditPackCheckoutSessionInput,
  ) => ReturnType<typeof createCreditPackCheckoutSession>;
  createScanPackCheckoutSession: (
    input: CreateScanPackCheckoutSessionInput,
  ) => ReturnType<typeof createScanPackCheckoutSession>;
  joinWaitlist: (input: JoinWaitlistInput) => ReturnType<typeof joinWaitlist>;
  getDashboard: () => ReturnType<typeof getDashboard>;
  dismissMemoSanityNotice: (
    input: DismissMemoSanityNoticeInput,
  ) => ReturnType<typeof dismissMemoSanityNotice>;
  dismissAssumptionNotice: (
    input: DismissAssumptionNoticeInput,
  ) => ReturnType<typeof dismissAssumptionNotice>;
  dismissExampleNotice: () => ReturnType<typeof dismissExampleNotice>;
  getFirstRunGuide: () => ReturnType<typeof getFirstRunGuide>;
  updateFirstRunGuide: (input: Parameters<typeof updateFirstRunGuide>[0]) => ReturnType<typeof updateFirstRunGuide>;
  setMemoCurrencyView: (
    input: SetMemoCurrencyViewInput,
  ) => ReturnType<typeof setMemoCurrencyView>;
  regenerateMyMemo: (input: RegenerateMyMemoInput) => ReturnType<typeof regenerateMyMemo>;
  generateSearchStrategy: (input: GenerateSearchStrategyInput) => ReturnType<typeof generateSearchStrategy>;
  getSearchStrategy: (input: GetSearchStrategyInput) => ReturnType<typeof getSearchStrategy>;
  startSearchReport: (input: StartSearchReportInput) => ReturnType<typeof startSearchReport>;
  getSearchReport: (input: GetSearchReportInput) => ReturnType<typeof getSearchReport>;
  getSearchReportPdf: (input: GetSearchReportPdfInput) => ReturnType<typeof getSearchReportPdf>;
  getSearchAccessStatus: (input: GetSearchAccessStatusInput) => ReturnType<typeof getSearchAccessStatus>;
  createSearchPlanCheckoutSession: (
    input: CreateSearchPlanCheckoutSessionInput,
  ) => ReturnType<typeof createSearchPlanCheckoutSession>;
  createSearchFullCheckoutSession: (
    input: CreateSearchFullCheckoutSessionInput,
  ) => ReturnType<typeof createSearchFullCheckoutSession>;
  // Public blog: all published posts (for the dynamic/freshness blog layer).
  listPublishedBlogPosts: () => ReturnType<typeof listPublishedBlogPosts>;
  // Public methodology page: real, honest aggregate work stats.
  getMethodologyStats: () => ReturnType<typeof getMethodologyStats>;
  getStrategyHub: () => ReturnType<typeof getStrategyHub>;
  submitFeedback: (input: SubmitFeedbackInput) => ReturnType<typeof submitFeedback>;
  listFeatureRequests: (input?: ListFeatureRequestsInput) => ReturnType<typeof listFeatureRequests>;
  getFeatureRequest: (input: GetFeatureRequestInput) => ReturnType<typeof getFeatureRequest>;
  submitFeatureRequest: (input: SubmitFeatureRequestInput) => ReturnType<typeof submitFeatureRequest>;
  voteFeatureRequest: (input: VoteFeatureRequestInput) => ReturnType<typeof voteFeatureRequest>;
  commentFeatureRequest: (input: CommentFeatureRequestInput) => ReturnType<typeof commentFeatureRequest>;

  // Surveys
  submitSurvey: (input: SubmitSurveyInput) => ReturnType<typeof submitSurvey>;
  getOnboardingState: () => ReturnType<typeof getOnboardingState>;
  reconnectDuplicateIdentity: () => ReturnType<typeof reconnectDuplicateIdentity>;
  getGuestDemo: () => ReturnType<typeof getGuestDemo>;
  getGuestDemoVerdict: (input: { key: string }) => ReturnType<typeof getGuestDemoVerdict>;

  // Admin (gated by 'admin' role on the server side)
  adminListUsers: () => ReturnType<typeof adminListUsers>;
  adminListDuplicateAccounts: () => ReturnType<typeof adminListDuplicateAccounts>;
  adminMergeAccounts: (input: {
    loserId: string;
    keeperId: string;
    dryRun?: boolean;
    unionRoles?: boolean;
    dropLabels?: string[];
    normalizeVisitorEmail?: boolean;
  }) => ReturnType<typeof adminMergeAccounts>;
  adminSendMergeOffer: (input: Parameters<typeof adminSendMergeOffer>[0]) => ReturnType<typeof adminSendMergeOffer>;
  getMergeOffer: (input: Parameters<typeof getMergeOffer>[0]) => ReturnType<typeof getMergeOffer>;
  checkMergeOfferPhone: (input: Parameters<typeof checkMergeOfferPhone>[0]) => ReturnType<typeof checkMergeOfferPhone>;
  acceptMergeOffer: (input: Parameters<typeof acceptMergeOffer>[0]) => ReturnType<typeof acceptMergeOffer>;
  adminGetUserDetail: (input: { userId: string }) => ReturnType<typeof adminGetUserDetail>;
  adminListSurveys: (input?: { type?: string }) => ReturnType<typeof adminListSurveys>;

  // Admin Support console (per-user, action-capable, content-free)
  getAdminUserSupportPanel: (input: { email?: string; userId?: string }) => ReturnType<typeof getAdminUserSupportPanel>;
  adminSearchUsers: (input: Parameters<typeof adminSearchUsers>[0]) => ReturnType<typeof adminSearchUsers>;
  grantAdminSessionCredit: (input: Parameters<typeof grantAdminSessionCredit>[0]) => ReturnType<typeof grantAdminSessionCredit>;
  grantAdminEntitlement: (input: Parameters<typeof grantAdminEntitlement>[0]) => ReturnType<typeof grantAdminEntitlement>;
  reverseAdminGrant: (input: Parameters<typeof reverseAdminGrant>[0]) => ReturnType<typeof reverseAdminGrant>;
  getPacket: (input: Parameters<typeof getPacket>[0]) => ReturnType<typeof getPacket>;
  createPacketShareLink: (input: Parameters<typeof createPacketShareLink>[0]) => ReturnType<typeof createPacketShareLink>;
  revokePacketShareLink: (input: Parameters<typeof revokePacketShareLink>[0]) => ReturnType<typeof revokePacketShareLink>;
  getSharedPacket: (input: Parameters<typeof getSharedPacket>[0]) => ReturnType<typeof getSharedPacket>;
  openSharedPacket: (input: Parameters<typeof openSharedPacket>[0]) => ReturnType<typeof openSharedPacket>;
  savePacketDraft: (input: Parameters<typeof savePacketDraft>[0]) => ReturnType<typeof savePacketDraft>;
  buildPacket: (input: Parameters<typeof buildPacket>[0], opts?: { stream?: boolean; onToken?: (text: string) => void }) => ReturnType<typeof buildPacket>;
  getPacketDownload: (input: Parameters<typeof getPacketDownload>[0]) => ReturnType<typeof getPacketDownload>;
  adminSetMemoPaid: (input: Parameters<typeof adminSetMemoPaid>[0]) => ReturnType<typeof adminSetMemoPaid>;
  grantAdminReferralCredit: (input: Parameters<typeof grantAdminReferralCredit>[0]) => ReturnType<typeof grantAdminReferralCredit>;
  addAdminCsNote: (input: { userId: string; text: string }) => ReturnType<typeof addAdminCsNote>;
  setAdminUserFlag: (input: { userId: string; flagged: boolean }) => ReturnType<typeof setAdminUserFlag>;
  setAdminUserInternal: (input: { userId: string; internal: boolean }) => ReturnType<typeof setAdminUserInternal>;
  setTesterPass: (input: { userId: string; enabled: boolean }) => ReturnType<typeof setTesterPass>;
  generateInfluencers: (input: Parameters<typeof generateInfluencers>[0]) => ReturnType<typeof generateInfluencers>;
  getInfluencerRun: (input: { runId: string }) => ReturnType<typeof getInfluencerRun>;
  cancelInfluencerRun: (input: { runId: string }) => ReturnType<typeof cancelInfluencerRun>;
  listInfluencerRuns: () => ReturnType<typeof listInfluencerRuns>;
  getInfluencerPipeline: () => ReturnType<typeof getInfluencerPipeline>;
  updateInfluencerCandidate: (input: Parameters<typeof updateInfluencerCandidate>[0]) => ReturnType<typeof updateInfluencerCandidate>;
  draftInfluencerOutreach: (input: Parameters<typeof draftInfluencerOutreach>[0]) => ReturnType<typeof draftInfluencerOutreach>;
  adjustAdminSubscription: (input: Parameters<typeof adjustAdminSubscription>[0]) => ReturnType<typeof adjustAdminSubscription>;
  listAdminFlagged: () => ReturnType<typeof listAdminFlagged>;
  adminListInventionFields: () => ReturnType<typeof adminListInventionFields>;
  getUnservedIndustryDemand: (
    input?: Parameters<typeof getUnservedIndustryDemand>[0],
  ) => ReturnType<typeof getUnservedIndustryDemand>;

  // Data Monitor (admin) — continuous-intelligence engine console
  getMonitorFeed: (
    input?: Parameters<typeof getMonitorFeed>[0],
  ) => ReturnType<typeof getMonitorFeed>;
  getMonitorEntities: () => ReturnType<typeof getMonitorEntities>;
  getMonitorEntityTimeline: (
    input: Parameters<typeof getMonitorEntityTimeline>[0],
  ) => ReturnType<typeof getMonitorEntityTimeline>;
  markMonitorAlert: (
    input: Parameters<typeof markMonitorAlert>[0],
  ) => ReturnType<typeof markMonitorAlert>;
  runMonitorSweep: () => ReturnType<typeof runMonitorSweep>;
  adminCreateNeedCluster: (
    input: AdminCreateNeedClusterInput,
  ) => ReturnType<typeof adminCreateNeedCluster>;
  adminScanNeedCluster: (
    input: AdminScanNeedClusterInput,
  ) => ReturnType<typeof adminScanNeedCluster>;
  adminManageNeedCluster: (
    input: AdminManageNeedClusterInput,
  ) => ReturnType<typeof adminManageNeedCluster>;
  adminAddIncubatorProgram: (
    input: AdminAddIncubatorProgramInput,
  ) => ReturnType<typeof adminAddIncubatorProgram>;
  adminListIncubatorPrograms: () => ReturnType<typeof adminListIncubatorPrograms>;
  adminRemoveIncubatorProgram: (
    input: AdminRemoveIncubatorProgramInput,
  ) => ReturnType<typeof adminRemoveIncubatorProgram>;

  // Survey templates (admin write, user-facing read)
  adminListSurveyTemplates: () => ReturnType<typeof adminListSurveyTemplates>;
  adminGetSurveyTemplate: (input: { slug: string }) => ReturnType<typeof adminGetSurveyTemplate>;
  adminSaveSurveyTemplate: (input: AdminSaveSurveyTemplateInput) => ReturnType<typeof adminSaveSurveyTemplate>;
  adminLaunchSurveyTemplate: (input: { id: string }) => ReturnType<typeof adminLaunchSurveyTemplate>;
  adminRetireSurveyTemplate: (input: { id: string }) => ReturnType<typeof adminRetireSurveyTemplate>;
  getActiveSurveyTemplate: (input: GetActiveSurveyTemplateInput) => ReturnType<typeof getActiveSurveyTemplate>;

  // Newsletter
  subscribeNewsletter: (input: SubscribeNewsletterInput) => ReturnType<typeof subscribeNewsletter>;
  unsubscribeNewsletter: () => ReturnType<typeof unsubscribeNewsletter>;
  getMyNewsletterStatus: () => ReturnType<typeof getMyNewsletterStatus>;
  adminListNewsletterStats: () => ReturnType<typeof adminListNewsletterStats>;

  // Inbound leads (admin)
  adminCreateLead: (input: Parameters<typeof adminCreateLead>[0]) => ReturnType<typeof adminCreateLead>;
  adminListLeads: () => ReturnType<typeof adminListLeads>;
  adminUpdateLead: (input: Parameters<typeof adminUpdateLead>[0]) => ReturnType<typeof adminUpdateLead>;

  adminListFeedback: (input?: { type?: 'general' | 'bug' | 'idea' }) => ReturnType<typeof adminListFeedback>;
  adminListMemoIssues: () => ReturnType<typeof adminListMemoIssues>;
  adminListPolicyNotices: () => ReturnType<typeof adminListPolicyNotices>;
  adminSendPolicyNotice: (input: Parameters<typeof adminSendPolicyNotice>[0]) => ReturnType<typeof adminSendPolicyNotice>;
  adminSeedExampleLibrary: (input?: Parameters<typeof adminSeedExampleLibrary>[0]) => ReturnType<typeof adminSeedExampleLibrary>;
  adminListExampleLibrary: () => ReturnType<typeof adminListExampleLibrary>;
  adminRegenerateMemo: (input: Parameters<typeof adminRegenerateMemo>[0]) => ReturnType<typeof adminRegenerateMemo>;
  adminNotifyMemoRestored: (input: Parameters<typeof adminNotifyMemoRestored>[0]) => ReturnType<typeof adminNotifyMemoRestored>;
  adminListContentPosts: (input?: Parameters<typeof adminListContentPosts>[0]) => ReturnType<typeof adminListContentPosts>;
  adminListKeywords: (input?: Parameters<typeof adminListKeywords>[0]) => ReturnType<typeof adminListKeywords>;
  adminReviewKeyword: (input: Parameters<typeof adminReviewKeyword>[0]) => ReturnType<typeof adminReviewKeyword>;
  adminAddKeyword: (input: Parameters<typeof adminAddKeyword>[0]) => ReturnType<typeof adminAddKeyword>;
  generateKeywordPostNow: (input: Parameters<typeof generateKeywordPostNow>[0]) => ReturnType<typeof generateKeywordPostNow>;
  adminListClusters: () => ReturnType<typeof adminListClusters>;
  adminCreateCluster: (input: Parameters<typeof adminCreateCluster>[0]) => ReturnType<typeof adminCreateCluster>;
  adminAssignClusterSpokes: (input: Parameters<typeof adminAssignClusterSpokes>[0]) => ReturnType<typeof adminAssignClusterSpokes>;
  adminRemoveClusterSpoke: (input: Parameters<typeof adminRemoveClusterSpoke>[0]) => ReturnType<typeof adminRemoveClusterSpoke>;
  adminSetClusterVelocity: (input: Parameters<typeof adminSetClusterVelocity>[0]) => ReturnType<typeof adminSetClusterVelocity>;
  adminDeleteCluster: (input: Parameters<typeof adminDeleteCluster>[0]) => ReturnType<typeof adminDeleteCluster>;
  adminGenerateClusterContent: (input: Parameters<typeof adminGenerateClusterContent>[0]) => ReturnType<typeof adminGenerateClusterContent>;
  adminCheckBlogOverlap: (input: Parameters<typeof adminCheckBlogOverlap>[0]) => ReturnType<typeof adminCheckBlogOverlap>;
  getDrawingBrief: (input: Parameters<typeof getDrawingBrief>[0]) => ReturnType<typeof getDrawingBrief>;
  getSketchWorkspace: (input: Parameters<typeof getSketchWorkspace>[0]) => ReturnType<typeof getSketchWorkspace>;
  getSketchUploadSlot: (input: Parameters<typeof getSketchUploadSlot>[0]) => ReturnType<typeof getSketchUploadSlot>;
  startSketchRun: (input: Parameters<typeof startSketchRun>[0]) => ReturnType<typeof startSketchRun>;
  pickSketchCandidate: (input: Parameters<typeof pickSketchCandidate>[0]) => ReturnType<typeof pickSketchCandidate>;
  getSketchDownload: (input: Parameters<typeof getSketchDownload>[0]) => ReturnType<typeof getSketchDownload>;
  getDiagramDownload: (input: Parameters<typeof getDiagramDownload>[0]) => ReturnType<typeof getDiagramDownload>;
  saveSketchAnchors: (input: Parameters<typeof saveSketchAnchors>[0]) => ReturnType<typeof saveSketchAnchors>;
  updateDrawingBriefMeta: (input: Parameters<typeof updateDrawingBriefMeta>[0]) => ReturnType<typeof updateDrawingBriefMeta>;
  draftDrawingBrief: (input: Parameters<typeof draftDrawingBrief>[0]) => ReturnType<typeof draftDrawingBrief>;
  saveDrawingBrief: (input: Parameters<typeof saveDrawingBrief>[0]) => ReturnType<typeof saveDrawingBrief>;
  refineDrawingBrief: (input: Parameters<typeof refineDrawingBrief>[0]) => ReturnType<typeof refineDrawingBrief>;
  listMemoAnnotations: (input: Parameters<typeof listMemoAnnotations>[0]) => ReturnType<typeof listMemoAnnotations>;
  saveMemoAnnotation: (input: Parameters<typeof saveMemoAnnotation>[0]) => ReturnType<typeof saveMemoAnnotation>;
  deleteMemoAnnotation: (input: Parameters<typeof deleteMemoAnnotation>[0]) => ReturnType<typeof deleteMemoAnnotation>;
  checkSessionMessage: (input: Parameters<typeof checkSessionMessage>[0]) => ReturnType<typeof checkSessionMessage>;
  // Secure Memo Sharing
  createMemoShareLink: (input: Parameters<typeof createMemoShareLink>[0]) => ReturnType<typeof createMemoShareLink>;
  listMemoShareLinks: (input: Parameters<typeof listMemoShareLinks>[0]) => ReturnType<typeof listMemoShareLinks>;
  revokeMemoShareLink: (input: Parameters<typeof revokeMemoShareLink>[0]) => ReturnType<typeof revokeMemoShareLink>;
  getSharedMemo: (input: Parameters<typeof getSharedMemo>[0]) => ReturnType<typeof getSharedMemo>;
  getSearchStrategyVersion: (input: Parameters<typeof getSearchStrategyVersion>[0]) => ReturnType<typeof getSearchStrategyVersion>;
  listDocAnnotations: (input: Parameters<typeof listDocAnnotations>[0]) => ReturnType<typeof listDocAnnotations>;
  saveDocAnnotation: (input: Parameters<typeof saveDocAnnotation>[0]) => ReturnType<typeof saveDocAnnotation>;
  deleteDocAnnotation: (input: Parameters<typeof deleteDocAnnotation>[0]) => ReturnType<typeof deleteDocAnnotation>;
  listLessonAnnotations: (input: Parameters<typeof listLessonAnnotations>[0]) => ReturnType<typeof listLessonAnnotations>;
  saveLessonAnnotation: (input: Parameters<typeof saveLessonAnnotation>[0]) => ReturnType<typeof saveLessonAnnotation>;
  deleteLessonAnnotation: (input: Parameters<typeof deleteLessonAnnotation>[0]) => ReturnType<typeof deleteLessonAnnotation>;
  makeAnnotationTask: (input: Parameters<typeof makeAnnotationTask>[0]) => ReturnType<typeof makeAnnotationTask>;
  listNotes: () => ReturnType<typeof listNotes>;
  saveQuickNote: (input: Parameters<typeof saveQuickNote>[0]) => ReturnType<typeof saveQuickNote>;
  updateNoteItem: (input: Parameters<typeof updateNoteItem>[0]) => ReturnType<typeof updateNoteItem>;
  deleteNoteItem: (input: Parameters<typeof deleteNoteItem>[0]) => ReturnType<typeof deleteNoteItem>;
  makeNoteTask: (input: Parameters<typeof makeNoteTask>[0]) => ReturnType<typeof makeNoteTask>;
  addPlanningTask: (input: Parameters<typeof addPlanningTask>[0]) => ReturnType<typeof addPlanningTask>;
  suggestPlanningTasks: (input: Parameters<typeof suggestPlanningTasks>[0]) => ReturnType<typeof suggestPlanningTasks>;
  saveCustomerDiscovery: (input: Parameters<typeof saveCustomerDiscovery>[0]) => ReturnType<typeof saveCustomerDiscovery>;
  refineDiscoveryQuestion: (input: Parameters<typeof refineDiscoveryQuestion>[0]) => ReturnType<typeof refineDiscoveryQuestion>;
  suggestBriefAssembly: (input: Parameters<typeof suggestBriefAssembly>[0]) => ReturnType<typeof suggestBriefAssembly>;
  dismissBriefChange: (input: Parameters<typeof dismissBriefChange>[0]) => ReturnType<typeof dismissBriefChange>;
  confirmDrawingBrief: (input: Parameters<typeof confirmDrawingBrief>[0]) => ReturnType<typeof confirmDrawingBrief>;
  keepDrawingBrief: (input: Parameters<typeof keepDrawingBrief>[0]) => ReturnType<typeof keepDrawingBrief>;
  getDrawingBriefPdf: (input: Parameters<typeof getDrawingBriefPdf>[0]) => ReturnType<typeof getDrawingBriefPdf>;
  listDesignSketches: () => ReturnType<typeof listDesignSketches>;
  createSketchCheckoutSession: (input: Parameters<typeof createSketchCheckoutSession>[0]) => ReturnType<typeof createSketchCheckoutSession>;
  registerDesignSetInterest: (input: Parameters<typeof registerDesignSetInterest>[0]) => ReturnType<typeof registerDesignSetInterest>;
  createDesignSetCheckoutSession: (input: Parameters<typeof createDesignSetCheckoutSession>[0]) => ReturnType<typeof createDesignSetCheckoutSession>;
  useDesignSetPack: (input: Parameters<typeof useDesignSetPack>[0]) => ReturnType<typeof useDesignSetPack>;
  startDrawingDraft: (input: Parameters<typeof startDrawingDraft>[0]) => ReturnType<typeof startDrawingDraft>;
  getDrawingDraft: (input: Parameters<typeof getDrawingDraft>[0]) => ReturnType<typeof getDrawingDraft>;
  updateDrawingDraft: (input: Parameters<typeof updateDrawingDraft>[0]) => ReturnType<typeof updateDrawingDraft>;
  setDrawingDraftReminder: (input: Parameters<typeof setDrawingDraftReminder>[0]) => ReturnType<typeof setDrawingDraftReminder>;
  resumeDrawingDraft: (input: Parameters<typeof resumeDrawingDraft>[0]) => ReturnType<typeof resumeDrawingDraft>;
  getGuestUploadSlot: (input: Parameters<typeof getGuestUploadSlot>[0]) => ReturnType<typeof getGuestUploadSlot>;
  claimDrawingDraft: (input: Parameters<typeof claimDrawingDraft>[0]) => ReturnType<typeof claimDrawingDraft>;
  listRelatedReading: (input: Parameters<typeof listRelatedReading>[0]) => ReturnType<typeof listRelatedReading>;
  adminSeedDrawingsClusters: () => ReturnType<typeof adminSeedDrawingsClusters>;
  adminNotifyDesignSetInterest: (input: Parameters<typeof adminNotifyDesignSetInterest>[0]) => ReturnType<typeof adminNotifyDesignSetInterest>;
  adminListSketchPurchases: (input: Parameters<typeof adminListSketchPurchases>[0]) => ReturnType<typeof adminListSketchPurchases>;
  getDetailDrawings: (input: Parameters<typeof getDetailDrawings>[0]) => ReturnType<typeof getDetailDrawings>;
  startDetailDrawing: (input: Parameters<typeof startDetailDrawing>[0]) => ReturnType<typeof startDetailDrawing>;
  getDetailDownload: (input: Parameters<typeof getDetailDownload>[0]) => ReturnType<typeof getDetailDownload>;
  saveDetailAnchor: (input: Parameters<typeof saveDetailAnchor>[0]) => ReturnType<typeof saveDetailAnchor>;
  makeDetailCurrent: (input: Parameters<typeof makeDetailCurrent>[0]) => ReturnType<typeof makeDetailCurrent>;
  getConceptModels: (input: Parameters<typeof getConceptModels>[0]) => ReturnType<typeof getConceptModels>;
  startConceptModel: (input: Parameters<typeof startConceptModel>[0]) => ReturnType<typeof startConceptModel>;
  confirmSketchAnchors: (input: Parameters<typeof confirmSketchAnchors>[0]) => ReturnType<typeof confirmSketchAnchors>;
  getDrawingFigureSheet: (input: Parameters<typeof getDrawingFigureSheet>[0]) => ReturnType<typeof getDrawingFigureSheet>;
  buildDrawingFigureSheet: (input: Parameters<typeof buildDrawingFigureSheet>[0]) => ReturnType<typeof buildDrawingFigureSheet>;
  getDrawingFigureSheetDownload: (input: Parameters<typeof getDrawingFigureSheetDownload>[0]) => ReturnType<typeof getDrawingFigureSheetDownload>;
  createModelAddonCheckoutSession: (input: Parameters<typeof createModelAddonCheckoutSession>[0]) => ReturnType<typeof createModelAddonCheckoutSession>;
  suggestRebuildParts: (input: Parameters<typeof suggestRebuildParts>[0]) => ReturnType<typeof suggestRebuildParts>;
  draftFixNote: (input: Parameters<typeof draftFixNote>[0]) => ReturnType<typeof draftFixNote>;
  getModelUploadSlot: (input: Parameters<typeof getModelUploadSlot>[0]) => ReturnType<typeof getModelUploadSlot>;
  saveModelUpload: (input: Parameters<typeof saveModelUpload>[0]) => ReturnType<typeof saveModelUpload>;
  proposeModelChecklist: (input: Parameters<typeof proposeModelChecklist>[0]) => ReturnType<typeof proposeModelChecklist>;
  getModelEditor: (input: Parameters<typeof getModelEditor>[0]) => ReturnType<typeof getModelEditor>;
  editConceptModel: (input: Parameters<typeof editConceptModel>[0]) => ReturnType<typeof editConceptModel>;
  getGuidedBuild: (input: Parameters<typeof getGuidedBuild>[0]) => ReturnType<typeof getGuidedBuild>;
  updateGuidedBuild: (input: Parameters<typeof updateGuidedBuild>[0]) => ReturnType<typeof updateGuidedBuild>;
  previewGuidedChecks: (input: Parameters<typeof previewGuidedChecks>[0]) => ReturnType<typeof previewGuidedChecks>;
  finishGuidedBuild: (input: Parameters<typeof finishGuidedBuild>[0]) => ReturnType<typeof finishGuidedBuild>;
  makeConceptModelCurrent: (input: Parameters<typeof makeConceptModelCurrent>[0]) => ReturnType<typeof makeConceptModelCurrent>;
  getConceptModelDownload: (input: Parameters<typeof getConceptModelDownload>[0]) => ReturnType<typeof getConceptModelDownload>;
  adminSeedConceptModel: (input: Parameters<typeof adminSeedConceptModel>[0]) => ReturnType<typeof adminSeedConceptModel>;
  adminConceptModelStats: () => ReturnType<typeof adminConceptModelStats>;
  buildFigureSheet: (input: Parameters<typeof buildFigureSheet>[0]) => ReturnType<typeof buildFigureSheet>;
  startRealisticRender: (input: Parameters<typeof startRealisticRender>[0]) => ReturnType<typeof startRealisticRender>;
  getDemoWalkthrough: (input: Parameters<typeof getDemoWalkthrough>[0]) => ReturnType<typeof getDemoWalkthrough>;
  adminAdvanceDemoWalkthrough: (input: Parameters<typeof adminAdvanceDemoWalkthrough>[0]) => ReturnType<typeof adminAdvanceDemoWalkthrough>;
  adminSetDemoWalkthroughLive: (input: Parameters<typeof adminSetDemoWalkthroughLive>[0]) => ReturnType<typeof adminSetDemoWalkthroughLive>;
  listLiveDemos: () => ReturnType<typeof listLiveDemos>;
  getPublicDemoStatus: (input: Parameters<typeof getPublicDemoStatus>[0]) => ReturnType<typeof getPublicDemoStatus>;
  adminListDemoWalkthroughs: () => ReturnType<typeof adminListDemoWalkthroughs>;
  getFigureSheetDownload: (input: Parameters<typeof getFigureSheetDownload>[0]) => ReturnType<typeof getFigureSheetDownload>;
  createComponentPackCheckoutSession: (input: Parameters<typeof createComponentPackCheckoutSession>[0]) => ReturnType<typeof createComponentPackCheckoutSession>;
  createRenderPackCheckoutSession: (input: Parameters<typeof createRenderPackCheckoutSession>[0]) => ReturnType<typeof createRenderPackCheckoutSession>;
  adminMatrixList: (input: Parameters<typeof adminMatrixList>[0]) => ReturnType<typeof adminMatrixList>;
  adminMatrixPreview: (input: Parameters<typeof adminMatrixPreview>[0]) => ReturnType<typeof adminMatrixPreview>;
  adminMatrixGenerate: (input: Parameters<typeof adminMatrixGenerate>[0]) => ReturnType<typeof adminMatrixGenerate>;
  adminMatrixSetCap: (input: Parameters<typeof adminMatrixSetCap>[0]) => ReturnType<typeof adminMatrixSetCap>;
  adminListInternalLinkSuggestions: (input?: Parameters<typeof adminListInternalLinkSuggestions>[0]) => ReturnType<typeof adminListInternalLinkSuggestions>;
  adminGenerateInternalLinks: (input: Parameters<typeof adminGenerateInternalLinks>[0]) => ReturnType<typeof adminGenerateInternalLinks>;
  adminResolveInternalLinkSuggestion: (input: Parameters<typeof adminResolveInternalLinkSuggestion>[0]) => ReturnType<typeof adminResolveInternalLinkSuggestion>;
  adminRunRetroactiveInternalLinks: (input?: Parameters<typeof adminRunRetroactiveInternalLinks>[0]) => ReturnType<typeof adminRunRetroactiveInternalLinks>;
  adminListCompetitors: () => ReturnType<typeof adminListCompetitors>;
  adminAddCompetitor: (input: Parameters<typeof adminAddCompetitor>[0]) => ReturnType<typeof adminAddCompetitor>;
  adminReviewCompetitor: (input: Parameters<typeof adminReviewCompetitor>[0]) => ReturnType<typeof adminReviewCompetitor>;
  adminDiscoverCompetitors: () => ReturnType<typeof adminDiscoverCompetitors>;
  adminRunKeywordEngine: (input?: Parameters<typeof adminRunKeywordEngine>[0]) => ReturnType<typeof adminRunKeywordEngine>;
  generateComparisonDraft: (input: Parameters<typeof generateComparisonDraft>[0]) => ReturnType<typeof generateComparisonDraft>;
  generateDataBriefDraft: (input: Parameters<typeof generateDataBriefDraft>[0]) => ReturnType<typeof generateDataBriefDraft>;
  adminApproveDataBrief: (input: Parameters<typeof adminApproveDataBrief>[0]) => ReturnType<typeof adminApproveDataBrief>;
  adminReviseComparisonDraft: (input: Parameters<typeof adminReviseComparisonDraft>[0]) => ReturnType<typeof adminReviseComparisonDraft>;
  applyToAffiliate: (input: Parameters<typeof applyToAffiliate>[0]) => ReturnType<typeof applyToAffiliate>;
  getMyAffiliate: () => ReturnType<typeof getMyAffiliate>;
  adminListAffiliates: () => ReturnType<typeof adminListAffiliates>;
  adminReviewAffiliate: (input: Parameters<typeof adminReviewAffiliate>[0]) => ReturnType<typeof adminReviewAffiliate>;
  adminUpdateAffiliateSettings: (input: Parameters<typeof adminUpdateAffiliateSettings>[0]) => ReturnType<typeof adminUpdateAffiliateSettings>;
  adminUpdateAffiliateProgramSettings: (input: Parameters<typeof adminUpdateAffiliateProgramSettings>[0]) => ReturnType<typeof adminUpdateAffiliateProgramSettings>;
  getAffiliateProgramInfo: () => ReturnType<typeof getAffiliateProgramInfo>;
  adminRecordAffiliatePayout: (input: Parameters<typeof adminRecordAffiliatePayout>[0]) => ReturnType<typeof adminRecordAffiliatePayout>;
  adminRunContentBatch: (input?: Parameters<typeof adminRunContentBatch>[0]) => ReturnType<typeof adminRunContentBatch>;
  adminGetContentBatchDefaults: () => ReturnType<typeof adminGetContentBatchDefaults>;
  adminSetContentBatchSkip: (
    input: Parameters<typeof adminSetContentBatchSkip>[0],
  ) => ReturnType<typeof adminSetContentBatchSkip>;
  adminListContentBatchRuns: (input?: Parameters<typeof adminListContentBatchRuns>[0]) => ReturnType<typeof adminListContentBatchRuns>;
  adminGetContentPost: (input: Parameters<typeof adminGetContentPost>[0]) => ReturnType<typeof adminGetContentPost>;
  adminCreateContentPost: (input: Parameters<typeof adminCreateContentPost>[0]) => ReturnType<typeof adminCreateContentPost>;
  adminUpdateContentPost: (input: Parameters<typeof adminUpdateContentPost>[0]) => ReturnType<typeof adminUpdateContentPost>;
  adminDeleteContentPost: (input: Parameters<typeof adminDeleteContentPost>[0]) => ReturnType<typeof adminDeleteContentPost>;
  adminGetBeehiivStatus: () => ReturnType<typeof adminGetBeehiivStatus>;
  adminPublishPostToBeehiiv: (input: Parameters<typeof adminPublishPostToBeehiiv>[0]) => ReturnType<typeof adminPublishPostToBeehiiv>;
  adminListVisitors: () => ReturnType<typeof adminListVisitors>;
  adminTrafficSources: () => ReturnType<typeof adminTrafficSources>;
  getManagerBoard: () => ReturnType<typeof getManagerBoard>;
  getManagerAsset: (input: Parameters<typeof getManagerAsset>[0]) => ReturnType<typeof getManagerAsset>;
  unlockManagerInvention: (input: Parameters<typeof unlockManagerInvention>[0]) => ReturnType<typeof unlockManagerInvention>;
  updateManagerAsset: (input: Parameters<typeof updateManagerAsset>[0]) => ReturnType<typeof updateManagerAsset>;
  createManagerCheckoutSession: (input: Parameters<typeof createManagerCheckoutSession>[0]) => ReturnType<typeof createManagerCheckoutSession>;
  adminManagerOverview: (input?: Parameters<typeof adminManagerOverview>[0]) => ReturnType<typeof adminManagerOverview>;
  adminPreviewCampaignAudience: (input: Parameters<typeof adminPreviewCampaignAudience>[0]) => ReturnType<typeof adminPreviewCampaignAudience>;
  adminPreviewCampaignEmail: (input: Parameters<typeof adminPreviewCampaignEmail>[0]) => ReturnType<typeof adminPreviewCampaignEmail>;
  adminGenerateCampaignContent: (input: Parameters<typeof adminGenerateCampaignContent>[0]) => ReturnType<typeof adminGenerateCampaignContent>;
  adminSaveCampaign: (input: Parameters<typeof adminSaveCampaign>[0]) => ReturnType<typeof adminSaveCampaign>;
  adminListCampaigns: () => ReturnType<typeof adminListCampaigns>;
  adminListFeaturableContent: () => ReturnType<typeof adminListFeaturableContent>;
  adminGetCampaign: (input: Parameters<typeof adminGetCampaign>[0]) => ReturnType<typeof adminGetCampaign>;
  adminListCampaignContent: () => ReturnType<typeof adminListCampaignContent>;
  adminSaveCampaignContent: (input: Parameters<typeof adminSaveCampaignContent>[0]) => ReturnType<typeof adminSaveCampaignContent>;
  adminDeleteCampaignContent: (input: Parameters<typeof adminDeleteCampaignContent>[0]) => ReturnType<typeof adminDeleteCampaignContent>;
  adminSendNewsletterWelcomeBacklog: (input: Parameters<typeof adminSendNewsletterWelcomeBacklog>[0]) => ReturnType<typeof adminSendNewsletterWelcomeBacklog>;
  generateSparkDirections: (input: Parameters<typeof generateSparkDirections>[0]) => ReturnType<typeof generateSparkDirections>;
  checkPatentability: (input: Parameters<typeof checkPatentability>[0]) => ReturnType<typeof checkPatentability>;
  adminRegenerateBlogCover: (input: Parameters<typeof adminRegenerateBlogCover>[0]) => ReturnType<typeof adminRegenerateBlogCover>;
  getPatentabilityAllowance: (input: Parameters<typeof getPatentabilityAllowance>[0]) => ReturnType<typeof getPatentabilityAllowance>;
  submitSparkLead: (input: Parameters<typeof submitSparkLead>[0]) => ReturnType<typeof submitSparkLead>;
  getSparkSession: (input: Parameters<typeof getSparkSession>[0]) => ReturnType<typeof getSparkSession>;
  generateSparkReport: (input: Parameters<typeof generateSparkReport>[0]) => ReturnType<typeof generateSparkReport>;
  getSparkWorkspace: () => ReturnType<typeof getSparkWorkspace>;
  listSparkRuns: () => ReturnType<typeof listSparkRuns>;
  sparkRunDirections: (input: Parameters<typeof sparkRunDirections>[0]) => ReturnType<typeof sparkRunDirections>;
  sparkGenerateMoreDirections: (input: Parameters<typeof sparkGenerateMoreDirections>[0]) => ReturnType<typeof sparkGenerateMoreDirections>;
  sparkRunReport: (input: Parameters<typeof sparkRunReport>[0]) => ReturnType<typeof sparkRunReport>;
  sparkConvertToIdea: (input: Parameters<typeof sparkConvertToIdea>[0]) => ReturnType<typeof sparkConvertToIdea>;
  claimSparkAnonymousSession: (input: Parameters<typeof claimSparkAnonymousSession>[0]) => ReturnType<typeof claimSparkAnonymousSession>;
  createSparkPackCheckoutSession: (input: Parameters<typeof createSparkPackCheckoutSession>[0]) => ReturnType<typeof createSparkPackCheckoutSession>;
  adminDeleteCampaign: (input: Parameters<typeof adminDeleteCampaign>[0]) => ReturnType<typeof adminDeleteCampaign>;
  adminSendTestEmail: (input: Parameters<typeof adminSendTestEmail>[0]) => ReturnType<typeof adminSendTestEmail>;
  adminSendCampaign: (input: Parameters<typeof adminSendCampaign>[0]) => ReturnType<typeof adminSendCampaign>;
  adminListLifecycleSegments: () => ReturnType<typeof adminListLifecycleSegments>;
  adminPreviewLifecycleCampaign: (input: Parameters<typeof adminPreviewLifecycleCampaign>[0]) => ReturnType<typeof adminPreviewLifecycleCampaign>;
  adminSendLifecycleCampaign: (input: Parameters<typeof adminSendLifecycleCampaign>[0]) => ReturnType<typeof adminSendLifecycleCampaign>;
  adminGetDashboardCore: () => ReturnType<typeof adminGetDashboardCore>;
  adminGetDashboardActivity: (input?: Parameters<typeof adminGetDashboardActivity>[0]) => ReturnType<typeof adminGetDashboardActivity>;
  // SEO Health Monitor
  adminGetSeoHealth: () => ReturnType<typeof adminGetSeoHealth>;
  adminGetSeoHealthSummary: () => ReturnType<typeof adminGetSeoHealthSummary>;
  adminCheckSeoRoute: (input: Parameters<typeof adminCheckSeoRoute>[0]) => ReturnType<typeof adminCheckSeoRoute>;
  adminRunSeoHealthSweep: () => ReturnType<typeof adminRunSeoHealthSweep>;
  adminRecheckFailingSeoRoutes: () => ReturnType<typeof adminRecheckFailingSeoRoutes>;
  adminGetCostsMonth: (input?: Parameters<typeof adminGetCostsMonth>[0]) => ReturnType<typeof adminGetCostsMonth>;
  adminGetCostsTrailing30: () => ReturnType<typeof adminGetCostsTrailing30>;
  adminGetOperationalCosts: (
    input?: Parameters<typeof adminGetOperationalCosts>[0],
  ) => ReturnType<typeof adminGetOperationalCosts>;
  // Rank Tracker
  adminGetRankTracker: () => ReturnType<typeof adminGetRankTracker>;
  adminManageRankKeywords: (
    input: Parameters<typeof adminManageRankKeywords>[0],
  ) => ReturnType<typeof adminManageRankKeywords>;
  adminRunRankTrackerNow: () => ReturnType<typeof adminRunRankTrackerNow>;
  adminGetRankInsights: () => ReturnType<typeof adminGetRankInsights>;
  adminGenerateKeywordVariations: (
    input: Parameters<typeof adminGenerateKeywordVariations>[0],
  ) => ReturnType<typeof adminGenerateKeywordVariations>;
  adminGetRevenueOverview: (input?: Parameters<typeof adminGetRevenueOverview>[0]) => ReturnType<typeof adminGetRevenueOverview>;
  adminGetRevenueBucketUsers: (input: Parameters<typeof adminGetRevenueBucketUsers>[0]) => ReturnType<typeof adminGetRevenueBucketUsers>;
  adminGetRevenueReconciliation: (input?: Parameters<typeof adminGetRevenueReconciliation>[0]) => ReturnType<typeof adminGetRevenueReconciliation>;
  adminGetRevenueCashMonth: (input?: Parameters<typeof adminGetRevenueCashMonth>[0]) => ReturnType<typeof adminGetRevenueCashMonth>;
  adminBackfillRevenueEvents: (input?: Parameters<typeof adminBackfillRevenueEvents>[0]) => ReturnType<typeof adminBackfillRevenueEvents>;
  unsubscribeByToken: (input: Parameters<typeof unsubscribeByToken>[0]) => ReturnType<typeof unsubscribeByToken>;

  // Alpha-cap signal — fires when the cap dialog opens for a visitor,
  // independent of whether they leave a beta-list email afterwards.
  recordAlphaCapHit: (input?: RecordAlphaCapHitInput) => ReturnType<typeof recordAlphaCapHit>;

  // Visitor heartbeat — pings every 30s while the tab is visible so
  // the admin can see how long each tester actually spent on the site.
  recordVisitorActivity: (input?: RecordVisitorActivityInput) => ReturnType<typeof recordVisitorActivity>;

  // Confidentiality terms acceptance — gates first idea capture and
  // first strategy session message. Sets a server-side timestamp so
  // we have a proof-of-acceptance record for legal inquiries.
  acknowledgeConfidentiality: (input?: AcknowledgeConfidentialityInput) => ReturnType<typeof acknowledgeConfidentiality>;
  syncMarketingConsent: (input: {
    granted: boolean;
    source: 'banner' | 'opt_out_region' | 'gpc' | 'preferences';
  }) => ReturnType<typeof syncMarketingConsent>;

  /** Renders the homepage hero cards to PNGs. Admin only. */
  adminExportHeroCards: (input: {
    css: string;
    rootVars?: string;
    cards: string[];
    background?: 'parchment' | 'transparent' | 'heartwood';
    debugHtml?: boolean;
  }) => ReturnType<typeof adminExportHeroCards>;

  // Family Bundle inquiry — public form on /students collects lead
  // data before Stripe billing for the bundle is wired.
  submitFamilyBundleInquiry: (input: SubmitFamilyBundleInquiryInput) => ReturnType<typeof submitFamilyBundleInquiry>;
  adminListFamilyBundleInquiries: () => ReturnType<typeof adminListFamilyBundleInquiries>;

  // Overflow purchase
  purchaseIdeaCredit: (input?: PurchaseIdeaCreditInput) => ReturnType<typeof purchaseIdeaCredit>;

  // Invention Vocabulary (glossary) — saving is subscriber-gated;
  // hover definitions themselves are free and need no backend.
  saveVocabularyTerm: (input: SaveVocabularyTermInput) => ReturnType<typeof saveVocabularyTerm>;
  removeVocabularyTerm: (input: RemoveVocabularyTermInput) => ReturnType<typeof removeVocabularyTerm>;
  listVocabulary: () => ReturnType<typeof listVocabulary>;
  listDueStudyCards: (input?: ListDueStudyCardsInput) => ReturnType<typeof listDueStudyCards>;
  gradeStudyCard: (input: GradeStudyCardInput) => ReturnType<typeof gradeStudyCard>;

  // My Invention Log — design decomposition. Free to use.
  getDesign: (input: GetDesignInput) => ReturnType<typeof getDesign>;
  dismissDesignMemoNotice: (input: Parameters<typeof dismissDesignMemoNotice>[0]) => ReturnType<typeof dismissDesignMemoNotice>;
  saveDesign: (input: SaveDesignInput) => ReturnType<typeof saveDesign>;
  listInventionLog: () => ReturnType<typeof listInventionLog>;
  // AI auto-decomposition — subscriber-gated. Returns the draft; the
  // frontend merges it into the workspace and autosaves.
  draftDesignDecomposition: (
    input: DraftDesignDecompositionInput,
  ) => ReturnType<typeof draftDesignDecomposition>;

  // Customer Discovery (Business Planning, tool 1). Generation is
  // subscriber-gated; getCustomerDiscovery (view/copy) is free.
  generateCustomerDiscovery: (
    input: GenerateCustomerDiscoveryInput,
  ) => ReturnType<typeof generateCustomerDiscovery>;
  getCustomerDiscovery: (
    input: GetCustomerDiscoveryInput,
  ) => ReturnType<typeof getCustomerDiscovery>;

  // Business Model Canvas (Business Planning, tool 2). Generation gated;
  // get / list / save / restore / export all free.
  generateBusinessCanvas: (
    input: GenerateBusinessCanvasInput,
  ) => ReturnType<typeof generateBusinessCanvas>;
  getBusinessCanvas: (
    input: GetBusinessCanvasInput,
  ) => ReturnType<typeof getBusinessCanvas>;
  listBusinessCanvasVersions: (
    input: ListBusinessCanvasVersionsInput,
  ) => ReturnType<typeof listBusinessCanvasVersions>;
  saveBusinessCanvasEdits: (
    input: SaveBusinessCanvasEditsInput,
  ) => ReturnType<typeof saveBusinessCanvasEdits>;
  restoreBusinessCanvasVersion: (
    input: RestoreBusinessCanvasVersionInput,
  ) => ReturnType<typeof restoreBusinessCanvasVersion>;
  generateCanvasAsset: (
    input: GenerateCanvasAssetInput,
  ) => ReturnType<typeof generateCanvasAsset>;
}

// Re-export the component shapes for frontend use.
export type { DesignComponent, MaterialOption, SeoHealthLedgerRow };

/**
 * The vendored `createClient()` from `@mindstudio-ai/interface` only
 * checks `body.error` when parsing failed responses, but the platform
 * actually returns `{code, errorString}`. As a result the SDK's
 * default `Method "X" failed: <status>` message wins every time a
 * backend method throws — none of our user-friendly messages reach
 * the user.
 *
 * Our own thin invoke wrapper:
 *  - Reads `errorString` from the platform's actual response shape
 *  - Falls back to the SDK behavior if no body is parsable
 *  - Preserves the same Error type that consumers already handle
 *
 * Once the upstream SDK is patched, we can rip this out and go back
 * to `createClient<ApiContract>()`.
 */
/**
 * Only reads are retried, and never mutations — a blip mid-read just
 * re-runs the read (no double-write risk), whereas retrying a write
 * whose outcome is unknown could silently double-apply it, which is a
 * worse failure than the one we're fixing. `get*` and `list*` are the
 * app's read conventions; `getOrCreate*` is an upsert (a write) and is
 * deliberately excluded even though it starts with `get`.
 */
function isRetryableRead(methodName: string): boolean {
  if (/^getOrCreate/i.test(methodName)) return false;
  return /^(get|list)/i.test(methodName);
}

/**
 * Whether an error is a transient platform/database blip worth retrying.
 * Mirrors the auth page's `isTransientPlatformError` so the whole app
 * agrees on what a "brief outage" looks like.
 *
 * The managed DB layer intermittently returns `owner_resolution_failed`
 * (503, "Could not resolve a stable owner for this database; retry") or
 * drops the socket (`[db] fetch failed`). These are infrastructure
 * hiccups, not app errors — a single one currently fails a whole page's
 * bootstrap. Retrying reads through them is the fix. Genuine errors
 * (validation, auth/401, not-found) never match, so they surface at
 * once with no added latency.
 */
function isTransientBlip(status: number | null, message: string): boolean {
  const m = (message || '').toLowerCase();
  if (typeof status === 'number' && status >= 500) return true;
  return (
    m.includes('owner_resolution_failed') ||
    m.includes('could not resolve a stable owner') ||
    m.includes('[db] fetch failed') ||
    m.includes('fetch failed') ||
    m.includes('failed to fetch') ||
    m.includes('network error') ||
    m.includes('networkerror') ||
    m.includes('bad gateway') ||
    m.includes('502') ||
    m.includes('503') ||
    m.includes('504') ||
    m.includes('err_timed_out') ||
    m.includes('timeout') ||
    m.includes('load failed')
  );
}

const sleepMs = (ms: number) => new Promise((r) => setTimeout(r, ms));

function createPatchedClient<T>(): T {
  const cfg = (globalThis as any).__MINDSTUDIO__;
  if (!cfg || !cfg.token || !cfg.methods) {
    // Fall back to the SDK's own error in this case so we don't
    // silently mask a real bootstrap problem.
    return createClient<T>();
  }

  return new Proxy({} as T & object, {
    get(_, methodName: string) {
      return async (
        input?: unknown,
        options?: { stream?: boolean; onToken?: (text: string) => void; onStreamError?: (err: string) => void },
      ) => {
        // Streaming path is rare and uses the same error shape; defer
        // to the SDK rather than re-implement SSE parsing here.
        if (options?.stream) {
          return (createClient<T>() as any)[methodName](input, options);
        }
        const methodId = cfg.methods[methodName];
        if (!methodId) {
          throw new Error(
            `Method "${methodName}" not found. Available: ${Object.keys(cfg.methods).join(', ') || '(none)'}`,
          );
        }

        // Retry ONLY reads through transient DB/gateway blips — a page
        // bootstrap must survive a hiccup. Mutations are never retried
        // (a single attempt), so a write can never silently double-run.
        const isRead = isRetryableRead(methodName);
        const maxAttempts = isRead ? 4 : 1;
        const backoffs = [300, 800, 1600];
        let lastErr: unknown;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          let res: Response;
          try {
            res = await fetch(`/_/methods/${methodId}/invoke`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${cfg.token}`,
              },
              body: JSON.stringify({ input: input ?? {} }),
            });
          } catch (netErr: any) {
            // The fetch itself rejected (offline, DNS, connection reset,
            // ERR_TIMED_OUT). Retry only if this is a read and there's an
            // attempt left.
            lastErr = netErr;
            if (isRead && attempt < maxAttempts - 1) {
              await sleepMs(backoffs[Math.min(attempt, backoffs.length - 1)]);
              continue;
            }
            throw netErr;
          }

          if (!res.ok) {
            // Try every shape the platform might return: `errorString`
            // (current platform), `error` (older SDK contract), then
            // fallback to status text.
            let message = `Method "${methodName}" failed: ${res.status} ${res.statusText}`;
            try {
              const body = await res.json();
              if (typeof body.errorString === 'string' && body.errorString.trim()) {
                message = body.errorString
                  // Trim platform prefix when present so the user sees
                  // just the original Error message we threw.
                  .replace(/^There was an error:\s*/, '');
              } else if (typeof body.error === 'string' && body.error.trim()) {
                message = body.error;
              } else if (
                body.error &&
                typeof body.error === 'object' &&
                typeof body.error.message === 'string'
              ) {
                message = body.error.message;
              }
            } catch {
              // Body wasn't JSON — keep the default
            }
            const err = new Error(message) as Error & { status?: number };
            err.status = res.status;

            // Retry a read on a transient blip; surface everything else
            // (401, 403, 404, validation) at once with no added latency.
            if (isRead && isTransientBlip(res.status, message) && attempt < maxAttempts - 1) {
              lastErr = err;
              await sleepMs(backoffs[Math.min(attempt, backoffs.length - 1)]);
              continue;
            }
            throw err;
          }

          const body = await res.json();
          return body.output;
        }

        // Exhausted every attempt on a transient error.
        throw lastErr;
      };
    },
  }) as T;
}

const rawClient = createPatchedClient<ApiContract>();

/**
 * Global 401 handler. The platform's `/_/methods` endpoint returns 401
 * (with no method invocation actually running, so it never appears in
 * the production request log) when the session cookie is missing or
 * expired. Without this wrapper, every page that fetches data on mount
 * shows a "401" toast and gets stuck on its loading skeleton.
 *
 * We can't wrap the SDK at fetch level (no hook is exposed), so we
 * proxy each method on the client and intercept errors as they bubble
 * back. On 401-like errors we:
 *   1. Clear the local auth store so React re-renders sign-in pages
 *   2. Call auth.logout() to flush the stale cookie
 *   3. Redirect to /landing if the user isn't already there
 *
 * Non-401 errors propagate untouched so existing per-page error
 * handling (toast, retry, etc.) stays intact.
 */
function isAuthExpiredError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  if (!msg) return false;
  if (/\b401\b/.test(msg)) return true;
  if (/unauthorized/i.test(msg)) return true;
  if (/not.?authenticated/i.test(msg)) return true;
  if (/invalid.?session/i.test(msg)) return true;
  // Valid session, but the app user row it points at no longer exists —
  // the account was deleted or merged away (e.g. by an admin duplicate
  // merge) while this browser still holds a live session cookie. Every
  // `requireUser()`-gated method (getDashboard, getOnboardingState, …)
  // throws this exact message, so the user boots into a half-alive state:
  // the session is valid so nothing routes them out, getMe returns no
  // profile, and every real data call errors forever. Signing in again
  // with the same address just re-mints a session for the same dead row,
  // which is what "tried to sign in and sign up but couldn't" looks like.
  // Treat it as an expired session: flush the stale cookie and land them
  // on a clean sign-in, where the platform re-provisions a fresh user row
  // on the next verification.
  if (/account not found/i.test(msg)) return true;
  return false;
}

let handlingExpiry = false;

async function handleAuthExpiry() {
  if (handlingExpiry) return;
  handlingExpiry = true;
  // Lazy-load to avoid a circular import: state.ts imports this file.
  try {
    const [{ useAuth }, { auth }] = await Promise.all([
      import('./state'),
      import('@mindstudio-ai/interface'),
    ]);
    try {
      await auth.logout();
    } catch {
      // already logged out — fine
    }
    useAuth.setState({ user: null, profile: null, ready: true });
    if (
      typeof window !== 'undefined' &&
      window.location.pathname !== '/landing' &&
      window.location.pathname !== '/signin' &&
      window.location.pathname !== '/'
    ) {
      window.location.href = '/landing';
    }
  } finally {
    // Allow a future expiry to be handled if the user signs back in
    // and their next session expires too.
    setTimeout(() => {
      handlingExpiry = false;
    }, 2000);
  }
}

const api = new Proxy(rawClient, {
  get(target, prop, receiver) {
    const original = Reflect.get(target, prop, receiver);
    if (typeof original !== 'function') return original;
    return (...args: unknown[]) => {
      const result = (original as Function).apply(target, args);
      if (result && typeof result.then === 'function') {
        return (result as Promise<unknown>).catch((err: unknown) => {
          if (isAuthExpiredError(err)) {
            void handleAuthExpiry();
          }
          throw err;
        });
      }
      return result;
    };
  },
}) as typeof rawClient;

export default api;
